import { Application } from 'pixi.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ASSETS_BASE, frameRect, loadManifest, walkFrames, type Manifest,
} from '../assets/manifest.js';
import { mapArtBodyScale, mapArtFootprint } from '../render/map-art.js';
import { TilemapView, type MapData, type TilesetDef } from '../render/tilemap.js';
import { sameFamily, shapedTileId, type Neighbors } from '../render/autotile-shape.js';
import { TilePalette } from './TilePalette.js';
import { TILE_SIZE, isAutotile } from '../render/tiles.js';
import { ZOOM_LEVELS } from '../render/zoom.js';
import {
  deleteMap,
  fetchMapData,
  fetchMapList,
  isEditorMapId,
  saveMap,
  type CustomMapData,
  type MapListItem,
  type MapObjective,
  type MapPlacement,
} from '../admin/mapApi.js';
// A partida e o editor decidem onde uma unidade cabe pela mesma conta: dois
// critérios para a mesma pergunta é como o editor propõe uma casa que o jogo
// não consegue jogar.
import { battleground, defaultLook, planDeployment } from '../game/setup.js';
import { useAuth } from '../auth/AuthContext.js';

export interface TerrainDefInfo {
  readonly name: string;
  readonly label: string;
  readonly color: string;
  readonly icon: string;
  readonly cost: number;
  readonly avoid: number;
  readonly defense: number;
  readonly heal: number;
  readonly impassable?: boolean;
}

export const TACTICAL_TERRAINS: Record<string, TerrainDefInfo> = {
  planicie: {
    name: 'planicie',
    label: 'Planície',
    color: 'rgba(100, 180, 80, 0.3)',
    icon: '🌱',
    cost: 1,
    avoid: 0,
    defense: 0,
    heal: 0,
  },
  estrada: {
    name: 'estrada',
    label: 'Estrada',
    color: 'rgba(215, 185, 120, 0.45)',
    icon: '═',
    cost: 1,
    avoid: 0,
    defense: 0,
    heal: 0,
  },
  floresta: {
    name: 'floresta',
    label: 'Floresta',
    color: 'rgba(34, 139, 34, 0.55)',
    icon: '🌲',
    cost: 2,
    avoid: 20,
    defense: 1,
    heal: 0,
  },
  colina: {
    name: 'colina',
    label: 'Colina',
    color: 'rgba(175, 115, 60, 0.5)',
    icon: '⛰️',
    cost: 2,
    avoid: 20,
    defense: 1,
    heal: 0,
  },
  montanha: {
    name: 'montanha',
    label: 'Montanha',
    color: 'rgba(110, 110, 125, 0.65)',
    icon: '🏔️',
    cost: 4,
    avoid: 30,
    defense: 2,
    heal: 0,
  },
  forte: {
    name: 'forte',
    label: 'Forte',
    color: 'rgba(45, 125, 230, 0.6)',
    icon: '🏰',
    cost: 2,
    avoid: 20,
    defense: 2,
    heal: 10,
  },
  ruina: {
    name: 'ruina',
    label: 'Ruína',
    color: 'rgba(145, 75, 185, 0.5)',
    icon: '🏛️',
    cost: 2,
    avoid: 20,
    defense: 1,
    heal: 0,
  },
  trono: {
    name: 'trono',
    label: 'Trono / Posto',
    color: 'rgba(235, 185, 30, 0.65)',
    icon: '👑',
    cost: 1,
    avoid: 20,
    defense: 2,
    heal: 10,
  },
  porta: {
    name: 'porta',
    label: 'Porta',
    color: 'rgba(180, 100, 40, 0.55)',
    icon: '🚪',
    cost: 1,
    avoid: 0,
    defense: 0,
    heal: 0,
  },
  piso: {
    name: 'piso',
    label: 'Piso Interior',
    color: 'rgba(160, 160, 175, 0.35)',
    icon: '◻️',
    cost: 1,
    avoid: 0,
    defense: 0,
    heal: 0,
  },
  muralha: {
    name: 'muralha',
    label: 'Muralha',
    color: 'rgba(215, 40, 40, 0.65)',
    icon: '🧱',
    cost: 0,
    avoid: 0,
    defense: 0,
    heal: 0,
    impassable: true,
  },
  agua: {
    name: 'agua',
    label: 'Água',
    color: 'rgba(30, 145, 235, 0.55)',
    icon: '🌊',
    cost: 0,
    avoid: 0,
    defense: 0,
    heal: 0,
    impassable: true,
  },
  abismo: {
    name: 'abismo',
    label: 'Abismo',
    color: 'rgba(20, 20, 25, 0.8)',
    icon: '🕳️',
    cost: 0,
    avoid: 0,
    defense: 0,
    heal: 0,
    impassable: true,
  },
};

type EditMode = 'tiles' | 'terrain' | 'placements' | 'ligacoes' | 'objective';

/**
 * O que o modo "Portais & Baús" está desenhando no momento.
 *
 * Os dois juntos num modo só porque são a mesma gramática — clicar numa casa
 * põe, clicar de novo na peça tira, arrastar move — e porque são as duas
 * coisas que o mapa tem ALÉM de terreno e tropa. Separá-los em dois modos
 * dobraria a barra sem dobrar o que se pode fazer.
 */
type LinkTool = 'teleporte' | 'bau';
type ToolType = 'pencil' | 'rect' | 'bucket' | 'eraser';

interface ValidationIssue {
  readonly level: 'error' | 'warning';
  readonly message: string;
}

/**
 * A mesma cor de terreno, com a opacidade reescalada.
 *
 * A paleta declara a intensidade cheia, usada nos quadradinhos da legenda. Sobre
 * o mapa ela precisa ser um véu: num calabouço quase toda casa é muralha, e a
 * 65% de vermelho o desenho inteiro desaparecia — era o "mapa distorcido".
 */
function washed(color: string, factor: number): string {
  const m = /^rgba?\(([^)]+)\)$/.exec(color.trim());
  if (!m) return color;
  const parts = m[1]!.split(',').map((v) => v.trim());
  const [r, g, b] = parts;
  const alpha = parts[3] === undefined ? 1 : Number(parts[3]);
  if (r === undefined || g === undefined || b === undefined || !Number.isFinite(alpha)) return color;
  return `rgba(${r}, ${g}, ${b}, ${(alpha * factor).toFixed(3)})`;
}

/** Por que o botão de salvar está fechado — em português, não em código. */
const SAVE_BLOCKED_REASON = (logged: boolean): string =>
  logged
    ? 'Somente administradores podem gravar mapas no servidor. Você ainda pode desenhar e testar o rascunho.'
    : 'Entre com uma conta de administrador para gravar mapas. Você ainda pode desenhar e testar o rascunho.';

/**
 * A casa transponível mais próxima de um ponto desejado.
 *
 * Um mapa do acervo tem água, muro e penhasco onde bem entende: cravar a
 * posição inicial numa coordenada calculada por fração da largura acerta em
 * cheio o meio de um lago com frequência desconfortável.
 */
function nearestOpenTile(
  map: Pick<CustomMapData, 'width' | 'height' | 'terrain' | 'terrainTypes'>,
  prefer: { x: number; y: number },
  taken: readonly { x: number; y: number }[],
): { x: number; y: number } {
  const blocked = (x: number, y: number): boolean => {
    const index = map.terrain[y * map.width + x] ?? 0;
    const name = map.terrainTypes[index] ?? 'planicie';
    return TACTICAL_TERRAINS[name]?.impassable === true;
  };
  const isTaken = (x: number, y: number): boolean => taken.some((t) => t.x === x && t.y === y);

  const candidates: { at: { x: number; y: number }; distance: number }[] = [];
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (blocked(x, y) || isTaken(x, y)) continue;
      candidates.push({
        at: { x, y },
        distance: Math.abs(x - prefer.x) + Math.abs(y - prefer.y),
      });
    }
  }
  candidates.sort((a, b) => a.distance - b.distance);
  return candidates[0]?.at ?? { x: 0, y: 0 };
}

/**
 * A anotação tática mínima de um mapa que chega sem nenhuma.
 *
 * Os 56 mapas importados do Saga de Distortidus trazem só arte e terreno. Sem
 * isto o editor os recebe com dois erros de validação e o botão de jogar
 * fechado — um muro logo na tela de abertura, em vez do ponto de partida que a
 * etapa promete: pegar um mapa pronto e anotá-lo.
 */
/**
 * Os seis comportamentos que o motor sabe jogar.
 *
 * A lista é a de `@oz/rules/ai`; o que vem junto aqui é a explicação em uma
 * linha, porque quem monta o mapa escolhe pelo efeito em campo e não pelo
 * nome do perfil.
 */
const AI_PROFILES = [
  { id: 'agressivo', label: '⚔️ Agressivo', hint: 'Persegue e ataca sempre que alcança alguém.' },
  { id: 'defensivo', label: '🛡️ Defensivo', hint: 'Prefere terreno vantajoso a avançar desprotegido.' },
  { id: 'guardiao', label: '🏯 Guardião', hint: 'Fica parado; só reage a quem entrar no alcance dele.' },
  { id: 'cacador', label: '🎯 Caçador', hint: 'Vai atrás de comandantes, curandeiros e feridos.' },
  { id: 'patrulha', label: '🧭 Patrulha', hint: 'Ronda o mapa até avistar um inimigo.' },
  { id: 'chefe', label: '👑 Chefe', hint: 'Não sai do posto; ataca quem chegar perto.' },
] as const;

function seedPlacements(
  map: CustomMapData,
  roster: { readonly className: string; readonly weaponName: string },
): MapPlacement[] {
  /*
   * As duas casas saem da MESMA conta que a partida faz.
   *
   * Antes saíam da casa aberta mais próxima do quarto do mapa, e "aberta" ali
   * queria dizer só "o terreno não é parede". Numa casa de chão cercada por
   * quatro paredes isso é verdade — e foi lá que o comandante do jogador de um
   * mapa importado nasceu, sem uma única jogada possível a partida inteira.
   * `planDeployment` só oferece casa de onde se alcança o resto do mapa.
   */
  const midY = Math.floor(map.height / 2);
  const plan = planDeployment(map, 1, 1);
  const heroAt =
    plan.player[0] ?? nearestOpenTile(map, { x: Math.floor(map.width / 4), y: midY }, []);
  const bossAt =
    plan.enemy[0] ??
    nearestOpenTile(map, { x: Math.floor((map.width * 3) / 4), y: midY }, [heroAt]);

  return [
    {
      name: 'Comandante Azul',
      team: 'jogador',
      className: roster.className,
      weaponName: roster.weaponName,
      level: 1,
      at: heroAt,
      isCommander: true,
    },
    {
      name: 'Chefe Inimigo',
      team: 'inimigo',
      className: roster.className,
      weaponName: roster.weaponName,
      level: 1,
      at: bossAt,
      isCommander: true,
      aiProfile: 'chefe',
    },
  ];
}

/**
 * O mapa do editor no formato que a escaramuça consome.
 *
 * `CustomMapData` deixa `id` e `shadows` opcionais porque um mapa em edição
 * pode não ter nenhum dos dois; o renderizador exige ambos.
 */
function toPlayableMap(map: CustomMapData): MapData {
  return {
    id: map.id ?? 0,
    name: map.name,
    width: map.width,
    height: map.height,
    tilesetId: map.tilesetId,
    layers: map.layers,
    shadows: map.shadows ?? new Array<number>(map.width * map.height).fill(0),
    terrain: map.terrain,
    terrainTypes: map.terrainTypes,
    // O ambiente acompanha o mapa até o jogo: sem isto, testar o traçado pelo
    // editor tocava a trilha errada e lutava sempre em campina.
    ...(map.bgm ? { bgm: map.bgm } : {}),
    ...(map.battleback ? { battleback: map.battleback } : {}),
    ...(map.battleback2 ? { battleback2: map.battleback2 } : {}),
    ...(map.teleports ? { teleports: map.teleports } : {}),
    // A paisagem e os elementos do acervo acompanham o mapa até o jogo: sem
    // isto, testar pelo editor abriria o mapa sem porta, sem baú e sem céu.
    ...(map.parallax ? { parallax: map.parallax } : {}),
    ...(map.events ? { events: map.events } : {}),
    ...(map.placements ? { placements: map.placements } : {}),
    ...(map.objective ? { objective: map.objective } : {}),
    ...(map.chests ? { chests: map.chests } : {}),
  };
}

export interface ChapterMapContext {
  readonly chapterNumber?: number | undefined;
  readonly chapterTitle?: string | undefined;
  readonly campaignName?: string | undefined;
  readonly recruits?: readonly {
    readonly characterId: number;
    readonly name: string;
    readonly recruitmentCondition?: string | undefined;
  }[] | undefined;
  /**
   * O capítulo passa a jogar NESTE mapa.
   *
   * Um mapa do acervo não é regravado pelo editor: salvar cria uma cópia com
   * id novo. Sem este aviso, o autor editava o mapa do capítulo, via "salvo com
   * sucesso" e o capítulo continuava jogando o original — as tropas que ele
   * acabara de posicionar não apareciam na batalha. Quem sabe repontar o
   * capítulo é o editor da história; o que falta aqui é contar que o id mudou.
   */
  readonly onMapChanged?: ((mapId: number, name: string) => void) | undefined;
  readonly onClose?: (() => void) | undefined;
}

export function MapEditor({
  onPlayMap,
  onPlayDraft,
  initialMapId,
  chapterContext,
}: {
  readonly onPlayMap?: (mapId: number) => void;
  readonly onPlayDraft?: (map: MapData) => void;
  readonly initialMapId?: number;
  readonly chapterContext?: ChapterMapContext;
}): JSX.Element {
  const { user } = useAuth();
  // Gravar mapa é ato administrativo (a API exige papel admin/dono). Saber
  // disso desde o começo é o que evita deixar alguém desenhar um mapa inteiro
  // para só então descobrir, num alerta, que nada podia ser salvo.
  const canPersist = user !== null && (user.role === 'admin' || user.role === 'dono');

  const hostRef = useRef<HTMLDivElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const pixiAppRef = useRef<Application | null>(null);
  const tilemapViewRef = useRef<TilemapView | null>(null);

  // Dados e navegação de mapas
  const [mapList, setMapList] = useState<readonly MapListItem[]>([]);
  const [selectedMapId, setSelectedMapId] = useState<number>(initialMapId ?? 13);

  useEffect(() => {
    if (initialMapId !== undefined && initialMapId !== selectedMapId) {
      setSelectedMapId(initialMapId);
    }
  }, [initialMapId]);

  const [currentMap, setCurrentMap] = useState<CustomMapData | null>(null);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [tilesets, setTilesets] = useState<readonly TilesetDef[]>([]);
  const [status, setStatus] = useState<string>('Carregando mapa…');
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Modos e ferramentas de edição
  const [editMode, setEditMode] = useState<EditMode>('terrain');
  const [tool, setTool] = useState<ToolType>('pencil');
  const [activeTerrain, setActiveTerrain] = useState<string>('floresta');
  // Pintura da arte do mapa: qual camada recebe o traço e qual tile está na mão.
  const [activeLayer, setActiveLayer] = useState<0 | 1 | 2 | 3>(0);
  const [activeTile, setActiveTile] = useState<number>(2816);
  // Sobe a cada traço de arte: é o sinal para o Pixi recozer o mapa. Comparar os
  // arrays camada a camada a cada quadro custaria mais do que o próprio desenho.
  const [tileRevision, setTileRevision] = useState(0);
  const [placementTeam, setPlacementTeam] = useState<'jogador' | 'inimigo' | 'aliado'>('jogador');
  const [selectedPlacementIdx, setSelectedPlacementIdx] = useState<number | null>(null);
  const [linkTool, setLinkTool] = useState<LinkTool>('teleporte');
  /*
   * A primeira boca de um portal em construção.
   *
   * Um teleporte é um PAR, e o gesto de criá-lo tem dois tempos: clicar na
   * entrada e clicar na saída. Entre um e outro, isto guarda a entrada — e é
   * também o que a tela pinta em destaque para dizer "agora escolha o outro
   * lado".
   */
  const [teleportDraft, setTeleportDraft] = useState<{ x: number; y: number } | null>(null);
  /** O item que o próximo baú vai guardar, e se ele nasce trancado. */
  /*
   * O prêmio do próximo baú, na forma "w:2" (arma) ou "i:31" (item do RPG).
   *
   * Um id solto não bastaria: o 7 é a Poção entre os itens e o Cajado de
   * Madeira entre as armas — a mesma razão de o motor guardar `weaponId` e
   * `itemId` em campos separados. Vazio é "o primeiro do acervo".
   */
  const [chestPrizeKey, setChestPrizeKey] = useState<string>('');
  const [chestLocked, setChestLocked] = useState<boolean>(true);
  /** O baú, portal ou tropa que o mouse está arrastando. */
  const [dragging, setDragging] = useState<
    | { kind: 'bau'; index: number }
    | { kind: 'teleporte'; index: number; side: 'a' | 'b' }
    | { kind: 'placement'; index: number }
    | null
  >(null);
  /** De onde o arrasto partiu — é o que separa mover de remover. */
  const [dragFrom, setDragFrom] = useState<{ x: number; y: number } | null>(null);
  const [targetSelectMode, setTargetSelectMode] = useState<boolean>(false);

  // Visualização e viewport
  const [zoom, setZoom] = useState<number>(1);
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [showTactical, setShowTactical] = useState<boolean>(true);
  // Quanto o véu tático cobre a arte. Anotar um mapa exige ver os dois ao mesmo
  // tempo, e o ponto de equilíbrio muda de mapa para mapa — quem anota decide.
  const [tacticalOpacity, setTacticalOpacity] = useState<number>(40);
  const [showUnits, setShowUnits] = useState<boolean>(true);

  /*
   * A arte das tropas desenhadas no mapa do editor.
   *
   * O tabuleiro do jogo é Pixi e carrega textura sozinho; este mapa é um canvas
   * 2D desenhado de uma vez, e `drawImage` não espera imagem chegar. O cache
   * guarda o que já veio, e uma imagem nova avisa que chegou pela revisão —
   * que é o que faz o traço acontecer de novo com a peça no lugar.
   */
  const unitArtRef = useRef(new Map<string, HTMLImageElement | null>());
  const [unitArtRevision, setUnitArtRevision] = useState(0);

  const unitArt = useCallback((url: string): HTMLImageElement | null => {
    const cache = unitArtRef.current;
    const known = cache.get(url);
    if (known !== undefined) return known;

    cache.set(url, null);
    const image = new Image();
    image.onload = () => {
      cache.set(url, image);
      setUnitArtRevision((r) => r + 1);
    };
    // Uma folha que não existe mais não pode ficar sendo pedida a cada traço:
    // fica marcada como ausente e a tropa cai no marcador de letra.
    image.onerror = () => cache.set(url, null);
    image.src = url;
    return null;
  }, []);

  /**
   * Com que desenho a tropa aparece no mapa do editor.
   *
   * A mesma escolha que a partida faz, e pela mesma razão: a folha de
   * caminhada quando ela existe, e o desenho de combate quando não — que é o
   * caso de todo monstro, porque o RPG Maker não dá folha de mapa a inimigo.
   * O editor mostrava um círculo com a inicial da classe para todo mundo, e
   * assim não havia como montar um mapa olhando para ele: goblin, dragão e
   * aldeão eram três discos vermelhos iguais.
   */
  const troopArt = useCallback(
    (
      unit: MapPlacement,
      index: number,
    ): {
      image: HTMLImageElement;
      crop: { x: number; y: number; width: number; height: number };
      width: number;
      height: number;
    } | null => {
      if (!manifest) return null;

      // A tropa que o mapa não vestiu entra em campo com a aparência padrão do
      // lado dela; o editor mostra a mesma, e não um disco com uma letra.
      const look = unit.sprite || unit.battler ? null : defaultLook(unit.team, index);
      const sheet = unit.sprite?.sheet ?? look?.sprite.sheet;
      const walk = sheet ? manifest.images['characters']?.[sheet] : undefined;
      if (walk?.grid) {
        const image = unitArt(`${ASSETS_BASE}/img/characters/${walk.file}`);
        if (!image) return null;
        // O quadro do meio da fileira de baixo: de pé, virado para quem olha.
        const frame = walkFrames(walk.grid, unit.sprite?.index ?? look?.sprite.index ?? 0, 0)[1] ?? 0;
        const crop = frameRect(walk.grid, frame);
        return { image, crop, width: crop.width, height: crop.height };
      }

      const battler = unit.battler ?? look?.battler;
      if (!battler) return null;
      for (const group of ['sv_enemies', 'enemies'] as const) {
        const art = manifest.images[group]?.[battler];
        if (!art) continue;
        const image = unitArt(`${ASSETS_BASE}/img/${group}/${art.file}`);
        if (!image) return null;
        // A mesma conta da partida: um monstro que vai ocupar quatro casas em
        // campo tem de ocupá-las aqui também, senão o mapa é montado às cegas.
        const scale = mapArtBodyScale(
          art.width, art.height, mapArtFootprint(art.width, art.height),
        );
        return {
          image,
          crop: { x: 0, y: 0, width: art.width, height: art.height },
          width: art.width * scale,
          height: art.height * scale,
        };
      }
      return null;
    },
    [manifest, unitArt],
  );

  // Mouse & interação
  const [hoverTile, setHoverTile] = useState<{ x: number; y: number } | null>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [rectStart, setRectStart] = useState<{ x: number; y: number } | null>(null);

  // Dados de apoio do jogo (classes e armas disponíveis)
  // A lista de reserva, para quando a API não responde, precisa conter nomes que
  // existam de fato no acervo: `buildSkirmish` procura a classe e a arma pelo
  // nome e lança se não achar. Nomes inventados aqui viravam uma escaramuça que
  // morria ao abrir, com "classe ausente no acervo".
  const [availableClasses, setAvailableClasses] = useState<Array<{ id: number; name: string }>>([
    { id: 1, name: 'Espadachim' },
    { id: 2, name: 'Mago' },
    { id: 3, name: 'Sacerdote' },
    { id: 4, name: 'Cavaleiro' },
    { id: 5, name: 'Artista Marcial' },
    { id: 6, name: 'Espadachim Mágico' },
    { id: 7, name: 'Arqueiro' },
    { id: 8, name: 'Arruaceiro' },
  ]);
  /** Os itens do RPG que podem ir dentro de um baú — poção, antídoto, chave. */
  const [availableItems, setAvailableItems] = useState<Array<{ id: number; name: string }>>([]);
  const [availableWeapons, setAvailableWeapons] = useState<Array<{ id: number; name: string }>>([
    { id: 1, name: 'Espada Curta' },
    { id: 2, name: 'Espada Longa' },
    { id: 3, name: 'Cajado de Madeira' },
    { id: 4, name: 'Malho de Bronze' },
  ]);
  const [availableCharacters, setAvailableCharacters] = useState<
    Array<{
      id: number;
      name: string;
      classId?: number;
      level?: number;
      portrait?: { sheet: string; index: number };
      mapSprite?: { sheet: string; index: number };
      battler?: string;
      equipment?: number[];
    }>
  >([
    { id: 1, name: 'Distortidus', classId: 8, level: 1, portrait: { sheet: 'Distortidus', index: 0 }, mapSprite: { sheet: 'distortidus', index: 0 }, battler: 'distortidus', equipment: [43, 2, 74] },
    { id: 2, name: 'Mondona', classId: 5, level: 3, portrait: { sheet: 'Mondona', index: 0 }, mapSprite: { sheet: 'mondona', index: 0 }, battler: 'mondona', equipment: [13, 2] },
    { id: 3, name: 'Lucretia', classId: 2, level: 6, portrait: { sheet: 'Actor1', index: 5 }, mapSprite: { sheet: 'Actor1', index: 5 }, battler: 'Actor1_6', equipment: [8, 40, 48, 4, 81] },
    { id: 4, name: 'Clodz', classId: 6, level: 5, portrait: { sheet: 'Distortidus', index: 1 }, mapSprite: { sheet: 'distortidus', index: 1 }, battler: 'Clodz', equipment: [2, 4, 75] },
    { id: 5, name: 'Dulpbuk', classId: 4, level: 7, portrait: { sheet: 'Distortidus', index: 2 }, mapSprite: { sheet: 'distortidus', index: 2 }, battler: 'Dulpbuk', equipment: [2, 27, 9] },
    { id: 6, name: 'Rednaux', classId: 7, level: 5, portrait: { sheet: 'team-naux', index: 0 }, mapSprite: { sheet: 'team-naux', index: 0 }, battler: 'team-naux-lutando', equipment: [38, 46, 3, 77] },
  ]);
  const [availableEnemies, setAvailableEnemies] = useState<
    Array<{
      id: number;
      name: string;
      battler?: string;
      mapSprite?: { sheet: string; index: number };
      move?: number;
      aiProfile?: string;
      exp?: number;
      gold?: number;
    }>
  >([
    { id: 1, name: 'Goblin', battler: 'Goblin', aiProfile: 'agressivo' },
    { id: 2, name: 'Gnomo', battler: 'Gnome', aiProfile: 'agressivo' },
    { id: 3, name: 'Pomba Preta', battler: 'Crow', aiProfile: 'agressivo' },
    { id: 4, name: 'Treant', battler: 'Treant', aiProfile: 'agressivo' },
    { id: 5, name: 'Hi_monstro', battler: 'Hi_monster', aiProfile: 'agressivo' },
    { id: 6, name: 'Zombie', battler: 'Zombie', aiProfile: 'agressivo' },
    { id: 8, name: 'Lobzius', battler: 'Wolfman', aiProfile: 'agressivo' },
  ]);
  const [activePlacementPreset, setActivePlacementPreset] = useState<string>('padrao');

  // Modal de Novo Mapa
  const [modalNewOpen, setModalNewOpen] = useState(false);
  const [newMapName, setNewMapName] = useState('Novo Mapa Tático');
  const [newMapWidth, setNewMapWidth] = useState(20);
  const [newMapHeight, setNewMapHeight] = useState(20);
  const [newMapTilesetId, setNewMapTilesetId] = useState(1);

  // 1. Carregamento inicial de manifesto, tilesets, classes, armas e lista de mapas
  useEffect(() => {
    let cancelled = false;

    Promise.all([
      loadManifest(),
      fetch(`${ASSETS_BASE}/content/tilesets.json`).then((r) => r.json() as Promise<TilesetDef[]>),
      fetchMapList().catch(() => []),
      fetch('/api/content/classes')
        .then((r) => r.json() as Promise<Array<{ id: number; name: string }>>)
        .catch(() => []),
      fetch('/api/content/weapons')
        .then((r) => r.json() as Promise<Array<{ id: number; name: string }>>)
        .catch(() => []),
      fetch('/api/content/items')
        .then((r) => r.json() as Promise<Array<{ id: number; name: string }>>)
        .catch(() => []),
      fetch('/api/content/characters')
        .then((r) => r.json() as Promise<any[]>)
        .catch(() => []),
      fetch('/api/content/enemies')
        .then((r) => r.json() as Promise<any[]>)
        .catch(() => []),
    ]).then(([m, ts, maps, cls, wpn, itm, chars, enemies]) => {
      if (cancelled) return;
      setManifest(m);
      setTilesets(ts);
      if (maps.length > 0) setMapList(maps);
      if (cls.length > 0) setAvailableClasses(cls);
      if (wpn.length > 0) setAvailableWeapons(wpn);
      if (itm.length > 0) setAvailableItems(itm);
      if (chars.length > 0) setAvailableCharacters(chars);
      if (enemies.length > 0) setAvailableEnemies(enemies);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * O prêmio escolhido no seletor, traduzido para o que o mapa grava.
   *
   * Sem escolha, a primeira arma do acervo — que é o que o editor sempre fez.
   */
  const chestPrizeFrom = useCallback(
    (key: string): { readonly weaponId: number } | { readonly itemId: number } | null => {
      const [kind, raw] = key.split(':');
      const id = Number(raw);
      if (kind === 'i' && id > 0) return { itemId: id };
      if (kind === 'w' && id > 0) return { weaponId: id };
      const first = availableWeapons[0];
      return first ? { weaponId: first.id } : null;
    },
    [availableWeapons],
  );

  /**
   * O nome do que está dentro de um baú, seja arma ou item do RPG.
   *
   * O editor mostra a mesma etiqueta na lista da barra lateral e no desenho
   * sobre o tabuleiro; procurar só entre as armas deixava todo baú de poção
   * rotulado como "item #31".
   */
  const chestPrizeName = useCallback(
    (chest: { readonly weaponId?: number | undefined; readonly itemId?: number | undefined }): string => {
      if (chest.itemId !== undefined) {
        return availableItems.find((i) => i.id === chest.itemId)?.name ?? `item #${chest.itemId}`;
      }
      return availableWeapons.find((w) => w.id === chest.weaponId)?.name ?? `arma #${chest.weaponId}`;
    },
    [availableItems, availableWeapons],
  );

  /** O tileset do mapa aberto — é dele que a paleta tira as folhas. */
  const currentTileset = useMemo(
    () =>
      currentMap
        ? tilesets.find((t) => t.id === currentMap.tilesetId) ?? tilesets[0] ?? null
        : null,
    [tilesets, currentMap],
  );

  /** Classe e arma padrão das forças semeadas — sempre nomes que existem. */
  const defaultRoster = useMemo(
    () => ({
      className: availableClasses[0]?.name ?? 'Espadachim',
      weaponName: availableWeapons[0]?.name ?? 'Espada Curta',
    }),
    [availableClasses, availableWeapons],
  );
  // Lido por referência dentro de `loadSelectedMap`: entrar na lista de
  // dependências faria o mapa recarregar toda vez que o acervo chegasse da API,
  // descartando a edição em curso.
  const rosterRef = useRef(defaultRoster);
  rosterRef.current = defaultRoster;

  // 2. Carregar mapa selecionado
  const loadSelectedMap = useCallback(
    async (id: number) => {
      setStatus(`Carregando mapa #${id}…`);
      try {
        const data = await fetchMapData(id);
        // Garante que terrain e terrainTypes estão inicializados
        const terrainTypes =
          data.terrainTypes && data.terrainTypes.length > 0
            ? [...data.terrainTypes]
            : ['planicie', 'muralha', 'agua'];

        // Garante que todas as chaves de TACTICAL_TERRAINS existam em terrainTypes
        for (const k of Object.keys(TACTICAL_TERRAINS)) {
          if (!terrainTypes.includes(k)) {
            terrainTypes.push(k);
          }
        }

        const size = data.width * data.height;
        const terrain = data.terrain ? [...data.terrain] : new Array<number>(size).fill(0);
        while (terrain.length < size) {
          terrain.push(0);
        }

        const base = { ...data, terrainTypes, terrain };
        const seeded = !data.placements || data.placements.length === 0;
        const placements = seeded ? seedPlacements(base, rosterRef.current) : [...data.placements];
        const objective = data.objective ?? { kind: 'derrotar_comandante' as const };

        setCurrentMap({ ...base, placements, objective });
        setSelectedPlacementIdx(null);
        setStatus(
          seeded
            ? `Mapa "${data.name}" aberto sem anotação tática — duas forças foram posicionadas para você ajustar.`
            : `Mapa "${data.name}" pronto para edição.`,
        );
      } catch (e) {
        setStatus(`Erro ao carregar mapa: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
    [],
  );

  useEffect(() => {
    if (selectedMapId) {
      loadSelectedMap(selectedMapId);
    }
  }, [selectedMapId, loadSelectedMap]);

  // 3. Montar / Atualizar PixiJS para a camada base do mapa
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !manifest || !currentMap || tilesets.length === 0) return;

    let app: Application | null = null;
    let view: TilemapView | null = null;
    let cancelled = false;
    // A limpeza do efeito pode chegar antes de `app.init` voltar. Destruir uma
    // aplicação Pixi ainda não inicializada estoura em "this._cancelResize is
    // not a function" e derruba a árvore React — a página fica em branco. Só o
    // corpo assíncrono sabe quando existe algo de verdade para desfazer.
    let ready = false;

    (async () => {
      const tileset = tilesets.find((t) => t.id === currentMap.tilesetId) ?? tilesets[0];
      if (!tileset) return;

      app = new Application();
      await app.init({
        width: currentMap.width * TILE_SIZE,
        height: currentMap.height * TILE_SIZE,
        backgroundAlpha: 0,
        antialias: false,
        roundPixels: true,
      });

      if (cancelled) {
        app.destroy(true);
        return;
      }

      view = new TilemapView();
      // Constrói MapData compatível com TilemapView
      const mapForView: MapData = {
        id: currentMap.id ?? 1,
        name: currentMap.name,
        width: currentMap.width,
        height: currentMap.height,
        tilesetId: currentMap.tilesetId,
        layers: currentMap.layers,
        shadows: currentMap.shadows ?? [],
        terrain: currentMap.terrain,
        terrainTypes: currentMap.terrainTypes,
        ...(currentMap.parallax ? { parallax: currentMap.parallax } : {}),
        ...(currentMap.events ? { events: currentMap.events } : {}),
      };

      await view.load(app.renderer, manifest, mapForView, tileset);
      if (cancelled) {
        app.destroy(true);
        return;
      }

      app.stage.addChild(view.container, view.overlay);
      app.ticker.add((ticker) => view?.update(ticker.deltaMS));

      host.replaceChildren(app.canvas);
      app.canvas.style.imageRendering = 'pixelated';
      app.canvas.style.display = 'block';

      ready = true;
      pixiAppRef.current = app;
      tilemapViewRef.current = view;
    })();

    return () => {
      cancelled = true;
      // Se ainda não está pronta, o próprio corpo assíncrono destrói ao ver
      // `cancelled` — aqui só sobraria a exceção.
      if (app && ready) {
        app.destroy(true);
        pixiAppRef.current = null;
        tilemapViewRef.current = null;
      }
    };
  }, [manifest, currentMap?.id, currentMap?.width, currentMap?.height, currentMap?.tilesetId, tilesets]);

  /**
   * Recozer o mapa depois de um traço de arte.
   *
   * A aplicação Pixi não é remontada: só o `TilemapView` recarrega. Derrubar a
   * aplicação a cada pincelada apagaria o canvas por um quadro e piscaria a
   * tela inteira enquanto se desenha.
   */
  useEffect(() => {
    if (tileRevision === 0) return;
    const app = pixiAppRef.current;
    const view = tilemapViewRef.current;
    if (!app || !view || !manifest || !currentMap) return;

    const tileset = tilesets.find((t) => t.id === currentMap.tilesetId) ?? tilesets[0];
    if (!tileset) return;

    let cancelled = false;
    void view
      .load(app.renderer, manifest, toPlayableMap(currentMap), tileset)
      .then(() => {
        if (cancelled || !pixiAppRef.current) return;
        app.stage.addChild(view.container, view.overlay);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
    // De propósito só a revisão: o mapa é lido por referência no momento do
    // traço, e incluí-lo aqui recozeria a cada tecla digitada no nome da tropa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileRevision]);

  // 4. Renderizar Canvas de Overlay Tático e Unidades
  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas || !currentMap) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height, terrain, terrainTypes, placements, objective } = currentMap;
    const pxW = width * TILE_SIZE;
    const pxH = height * TILE_SIZE;

    if (canvas.width !== pxW || canvas.height !== pxH) {
      canvas.width = pxW;
      canvas.height = pxH;
    }

    ctx.clearRect(0, 0, pxW, pxH);

    // A) Grade
    if (showGrid) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
      ctx.lineWidth = 1;
      for (let x = 0; x <= width; x++) {
        ctx.beginPath();
        ctx.moveTo(x * TILE_SIZE + 0.5, 0);
        ctx.lineTo(x * TILE_SIZE + 0.5, pxH);
        ctx.stroke();
      }
      for (let y = 0; y <= height; y++) {
        ctx.beginPath();
        ctx.moveTo(0, y * TILE_SIZE + 0.5);
        ctx.lineTo(pxW, y * TILE_SIZE + 0.5);
        ctx.stroke();
      }
    }

    // B) Terreno Tático
    if (showTactical) {
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const idx = y * width + x;
          const tCode = terrain[idx] ?? 0;
          const tName = terrainTypes[tCode] ?? 'planicie';
          const info = TACTICAL_TERRAINS[tName];

          if (info && tName !== 'planicie') {
            const px = x * TILE_SIZE;
            const py = y * TILE_SIZE;
            const factor = tacticalOpacity / 100;

            ctx.fillStyle = washed(info.color, factor);
            ctx.fillRect(px, py, TILE_SIZE, TILE_SIZE);

            // A borda mantém o tipo legível mesmo com o véu quase transparente:
            // é ela, e não o preenchimento, que desenha o contorno das regiões.
            ctx.strokeStyle = washed(info.color, Math.min(1, factor * 2.2));
            ctx.lineWidth = 2;
            ctx.strokeRect(px + 1, py + 1, TILE_SIZE - 2, TILE_SIZE - 2);

            // Ícone com sombra: sobre arte clara, emoji branco sumia.
            ctx.save();
            ctx.globalAlpha = Math.min(1, 0.35 + factor);
            ctx.shadowColor = 'rgba(0, 0, 0, 0.85)';
            ctx.shadowBlur = 3;
            ctx.fillStyle = '#ffffff';
            ctx.fillText(info.icon, px + TILE_SIZE / 2, py + TILE_SIZE / 2);
            ctx.restore();
          }
        }
      }
    }

    // C) Posição Alvo da Missão (se objetivo for tomar_posicao)
    if (objective?.kind === 'tomar_posicao' && objective.at) {
      const { x, y } = objective.at;
      if (x >= 0 && x < width && y >= 0 && y < height) {
        ctx.save();
        ctx.fillStyle = 'rgba(255, 215, 0, 0.4)';
        ctx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
        ctx.strokeStyle = '#ffd700';
        ctx.lineWidth = 3;
        ctx.strokeRect(x * TILE_SIZE + 2, y * TILE_SIZE + 2, TILE_SIZE - 4, TILE_SIZE - 4);
        ctx.font = 'bold 11px sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.fillText('🎯 ALVO', x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE - 6);
        ctx.restore();
      }
    }

    /*
     * C2) Portais e baús.
     *
     * Desenhados SEMPRE, e não só no modo que os edita: eles mudam como o
     * mapa se joga — o portal liga dois cômodos que a grade separa, o baú
     * pede um desvio — e anotar tropa ou terreno sem enxergá-los é anotar às
     * cegas. Foi assim que mapas do acervo saíram do editor com o esquadrão
     * plantado do lado errado de uma ligação.
     */
    for (const [index, link] of (currentMap?.teleports ?? []).entries()) {
      const bocas = [link.a, link.b];
      const cores = ['#a78bfa', '#c4b5fd'];
      ctx.save();
      for (const [lado, boca] of bocas.entries()) {
        if (boca.x < 0 || boca.x >= width || boca.y < 0 || boca.y >= height) continue;
        const px = boca.x * TILE_SIZE;
        const py = boca.y * TILE_SIZE;
        ctx.fillStyle = 'rgba(139, 92, 246, 0.32)';
        ctx.fillRect(px, py, TILE_SIZE, TILE_SIZE);
        ctx.strokeStyle = cores[lado]!;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 3]);
        ctx.strokeRect(px + 2, py + 2, TILE_SIZE - 4, TILE_SIZE - 4);
        ctx.setLineDash([]);
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#f5f3ff';
        ctx.fillText('🌀', px + TILE_SIZE / 2, py + TILE_SIZE / 2);
        ctx.font = 'bold 9px ui-monospace, monospace';
        ctx.fillText(`${index + 1}${lado === 0 ? 'a' : 'b'}`, px + TILE_SIZE / 2, py + TILE_SIZE - 6);
      }
      // A linha entre as duas bocas: sem ela, dez portais num mapa viram vinte
      // marcas soltas e ninguém sabe qual leva a qual.
      ctx.beginPath();
      ctx.moveTo(link.a.x * TILE_SIZE + TILE_SIZE / 2, link.a.y * TILE_SIZE + TILE_SIZE / 2);
      ctx.lineTo(link.b.x * TILE_SIZE + TILE_SIZE / 2, link.b.y * TILE_SIZE + TILE_SIZE / 2);
      ctx.strokeStyle = 'rgba(167, 139, 250, 0.55)';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 6]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // A boca à espera do par, enquanto o portal está pela metade.
    if (teleportDraft && editMode === 'ligacoes') {
      ctx.save();
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 3;
      ctx.strokeRect(
        teleportDraft.x * TILE_SIZE + 1,
        teleportDraft.y * TILE_SIZE + 1,
        TILE_SIZE - 2,
        TILE_SIZE - 2,
      );
      ctx.restore();
    }

    for (const chest of currentMap?.chests ?? []) {
      const { x, y } = chest.at;
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      const px = x * TILE_SIZE;
      const py = y * TILE_SIZE;
      const label = chestPrizeName(chest);
      ctx.save();
      ctx.fillStyle = 'rgba(217, 119, 6, 0.32)';
      ctx.fillRect(px, py, TILE_SIZE, TILE_SIZE);
      ctx.strokeStyle = chest.locked === false ? '#fcd34d' : '#f59e0b';
      ctx.lineWidth = 2;
      ctx.strokeRect(px + 2, py + 2, TILE_SIZE - 4, TILE_SIZE - 4);
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(chest.locked === false ? '📦' : '🧰', px + TILE_SIZE / 2, py + TILE_SIZE / 2 - 3);
      ctx.font = 'bold 8px sans-serif';
      ctx.fillStyle = '#fff7ed';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
      ctx.shadowBlur = 3;
      ctx.fillText(label.slice(0, 10), px + TILE_SIZE / 2, py + TILE_SIZE - 5);
      ctx.restore();
    }

    // D) Unidades e Tropas
    if (showUnits && placements) {
      placements.forEach((u, i) => {
        const { x, y } = u.at;
        if (x < 0 || x >= width || y < 0 || y >= height) return;

        const isSelected = i === selectedPlacementIdx;
        const cx = x * TILE_SIZE + TILE_SIZE / 2;
        const cy = y * TILE_SIZE + TILE_SIZE / 2;
        const chao = y * TILE_SIZE + TILE_SIZE;
        const radius = 18;

        // Cor de fundo do time
        let fill = '#3182ce'; // Jogador (azul)
        if (u.team === 'inimigo') fill = '#e53e3e'; // Inimigo (vermelho)
        if (u.team === 'aliado') fill = '#38a169'; // Aliado (verde)

        // A mesma cor, clara o bastante para o nome se ler sobre grama, pedra
        // ou água — o tom cheio do disco some em metade dos mapas.
        let ink = '#8ec5ff';
        if (u.team === 'inimigo') ink = '#ff9a92';
        if (u.team === 'aliado') ink = '#86e2ab';

        const art = troopArt(u, i);

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        let topo: number;

        if (art) {
          /*
           * A arte diz QUEM é; a base no chão diz DE QUE LADO.
           *
           * O disco cheio de antes teria de ficar por cima do desenho para
           * continuar legível — e aí esconde justamente o que se veio ver. A
           * base é mais larga que os pés de propósito: assim ela escapa por
           * baixo de qualquer sprite, do ovo ao dragão, e ainda ancora na casa
           * a peça que transborda dela.
           */
          ctx.beginPath();
          ctx.ellipse(cx, chao - 4, TILE_SIZE * 0.42, TILE_SIZE * 0.16, 0, 0, Math.PI * 2);
          ctx.globalAlpha = 0.55;
          ctx.fillStyle = fill;
          ctx.fill();
          ctx.globalAlpha = 1;
          ctx.lineWidth = 2;
          ctx.strokeStyle = fill;
          ctx.stroke();

          topo = chao + 2 - art.height;
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(
            art.image,
            art.crop.x, art.crop.y, art.crop.width, art.crop.height,
            cx - art.width / 2, topo, art.width, art.height,
          );

          // A seleção é da CASA, e não da figura: um dragão de casa e meia
          // sairia com um contorno maior que o tile e ninguém saberia dizer
          // qual das três casas está selecionada.
          if (isSelected) {
            ctx.setLineDash([5, 3]);
            ctx.lineWidth = 3;
            ctx.strokeStyle = '#ffd700';
            ctx.strokeRect(x * TILE_SIZE + 2, y * TILE_SIZE + 2, TILE_SIZE - 4, TILE_SIZE - 4);
            ctx.setLineDash([]);
          }
        } else {
          // Sem arte no acervo — a tropa ainda precisa aparecer.
          ctx.beginPath();
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
          ctx.fillStyle = fill;
          ctx.fill();
          ctx.lineWidth = isSelected ? 3 : 2;
          ctx.strokeStyle = isSelected ? '#ffd700' : '#ffffff';
          ctx.stroke();

          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 12px sans-serif';
          ctx.fillText(u.className ? u.className.charAt(0).toUpperCase() : 'U', cx, cy - 1);
          topo = cy - radius;
        }

        // Badge de Comandante (👑)
        if (u.isCommander) {
          ctx.font = '14px sans-serif';
          ctx.fillText('👑', cx, topo - 8);
        }

        // Nome compacto abaixo, na cor do lado: com a arte real no lugar do
        // disco colorido, é o nome que passa a carregar a informação de time.
        ctx.font = 'bold 9px sans-serif';
        ctx.fillStyle = art ? ink : '#f7fafc';
        ctx.shadowColor = '#000000';
        ctx.shadowBlur = 4;
        const shortName = u.name.length > 8 ? u.name.substring(0, 7) + '…' : u.name;
        // Abaixo da base, não em cima dela.
        ctx.fillText(shortName, cx, chao + (art ? 12 : 6));
        ctx.restore();
      });
    }

    // E) Retângulo de Seleção em Arraste (ferramenta rect)
    if (tool === 'rect' && rectStart && hoverTile) {
      const minX = Math.min(rectStart.x, hoverTile.x);
      const maxX = Math.max(rectStart.x, hoverTile.x);
      const minY = Math.min(rectStart.y, hoverTile.y);
      const maxY = Math.max(rectStart.y, hoverTile.y);

      ctx.save();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.fillRect(
        minX * TILE_SIZE,
        minY * TILE_SIZE,
        (maxX - minX + 1) * TILE_SIZE,
        (maxY - minY + 1) * TILE_SIZE,
      );
      ctx.strokeStyle = '#63b3ed';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(
        minX * TILE_SIZE,
        minY * TILE_SIZE,
        (maxX - minX + 1) * TILE_SIZE,
        (maxY - minY + 1) * TILE_SIZE,
      );
      ctx.restore();
    }

    // E.1) Destaque do elemento em arrasto (drag'n'drop)
    if (dragging && hoverTile && hoverTile.x >= 0 && hoverTile.x < width && hoverTile.y >= 0 && hoverTile.y < height) {
      ctx.save();
      ctx.strokeStyle = '#48bb78';
      ctx.lineWidth = 3;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(hoverTile.x * TILE_SIZE + 1, hoverTile.y * TILE_SIZE + 1, TILE_SIZE - 2, TILE_SIZE - 2);
      ctx.fillStyle = 'rgba(72, 187, 120, 0.28)';
      ctx.fillRect(hoverTile.x * TILE_SIZE + 1, hoverTile.y * TILE_SIZE + 1, TILE_SIZE - 2, TILE_SIZE - 2);
      ctx.restore();
    }

    // F) Cursor de Hover sob o Mouse
    if (hoverTile && hoverTile.x >= 0 && hoverTile.x < width && hoverTile.y >= 0 && hoverTile.y < height) {
      ctx.save();
      ctx.strokeStyle = targetSelectMode ? '#ffd700' : '#ffff66';
      ctx.lineWidth = 2;
      ctx.strokeRect(hoverTile.x * TILE_SIZE, hoverTile.y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
      ctx.restore();
    }
  }, [
    currentMap,
    showGrid,
    showTactical,
    tacticalOpacity,
    showUnits,
    hoverTile,
    rectStart,
    tool,
    selectedPlacementIdx,
    targetSelectMode,
    troopArt,
    // Uma folha que acabou de chegar do servidor não muda nada do mapa — muda
    // só o que dá para desenhar dele. Sem esta dependência as tropas ficariam
    // como círculos até o próximo clique.
    unitArtRevision,
    // A boca de portal à espera do par e o catálogo que nomeia o item do baú:
    // nenhum dos dois vive no mapa, e sem eles o desenho fica para trás.
    teleportDraft,
    dragging,
    editMode,
    availableWeapons,
  ]);

  // 5. Validação Tática em Tempo Real
  const validationIssues = useMemo<ValidationIssue[]>(() => {
    if (!currentMap) return [];
    const issues: ValidationIssue[] = [];
    const { placements, objective, terrain, terrainTypes, width, height } = currentMap;

    const playerCount = placements?.filter((p) => p.team === 'jogador').length ?? 0;
    const enemyCount = placements?.filter((p) => p.team === 'inimigo').length ?? 0;

    if (playerCount === 0) {
      issues.push({ level: 'error', message: 'Nenhuma unidade do Jogador posicionada (mínimo 1).' });
    }
    if (enemyCount === 0) {
      issues.push({ level: 'error', message: 'Nenhuma unidade Inimiga posicionada (mínimo 1).' });
    }

    /*
     * Unidade fora do campo de batalha é unidade que não joga.
     *
     * Uma casa de chão cercada por quatro paredes passa em qualquer conferência
     * de terreno e é, na prática, uma cela: a unidade abre a partida sem jogada
     * possível, e se for o comandante do objetivo a partida também não termina.
     * A partida realoca quem cai nessa situação, mas dizer aqui é melhor do que
     * consertar calado — quem monta o mapa quer saber que a casa não serve.
     */
    if (placements && placements.length > 0) {
      const campo = battleground(currentMap);
      const presas = placements.filter((p) => !campo.has(p.at.y * width + p.at.x));
      if (presas.length > 0) {
        issues.push({
          level: 'warning',
          message:
            `${presas.length} unidade(s) em casa isolada do resto do mapa ` +
            `(${presas.map((p) => `${p.name} em ${p.at.x},${p.at.y}`).join('; ')}). ` +
            'Elas não conseguem se mover nem alcançar o inimigo; ao jogar, serão realocadas.',
        });
      }
    }

    if (objective?.kind === 'derrotar_comandante') {
      const hasCommander = placements?.some((p) => p.team === 'inimigo' && p.isCommander);
      if (!hasCommander) {
        issues.push({
          level: 'error',
          message: 'O objetivo exige derrotar o Comandante, mas nenhum inimigo está marcado como Comandante.',
        });
      }
    }

    if (objective?.kind === 'tomar_posicao') {
      if (!objective.at) {
        issues.push({ level: 'error', message: 'Posição alvo do objetivo não foi definida.' });
      } else {
        const { x, y } = objective.at;
        if (x < 0 || x >= width || y < 0 || y >= height) {
          issues.push({ level: 'error', message: 'A posição alvo do objetivo está fora dos limites do mapa.' });
        } else {
          const tIdx = terrain[y * width + x] ?? 0;
          const tName = terrainTypes[tIdx] ?? 'planicie';
          const info = TACTICAL_TERRAINS[tName];
          if (info?.impassable) {
            issues.push({
              level: 'error',
              message: `A posição alvo está sobre terreno intransponível (${info.label}).`,
            });
          }
        }
      }
    }

    // Verifica unidades em terreno intransponível
    placements?.forEach((p) => {
      const { x, y } = p.at;
      if (x >= 0 && x < width && y >= 0 && y < height) {
        const tIdx = terrain[y * width + x] ?? 0;
        const tName = terrainTypes[tIdx] ?? 'planicie';
        const info = TACTICAL_TERRAINS[tName];
        if (info?.impassable) {
          issues.push({
            level: 'warning',
            message: `A unidade "${p.name}" (${p.team}) está sobre ${info.label} (intransponível).`,
          });
        }
      }
    });

    // Verifica sobreposição de unidades
    const positions = new Set<string>();
    placements?.forEach((p) => {
      const key = `${p.at.x},${p.at.y}`;
      if (positions.has(key)) {
        issues.push({
          level: 'error',
          message: `Há mais de uma unidade sobreposta na coordenada (${p.at.x}, ${p.at.y}).`,
        });
      }
      positions.add(key);
    });

    return issues;
  }, [currentMap]);

  const hasCriticalErrors = validationIssues.some((i) => i.level === 'error');

  // 6. Manipulação de Coordenadas e Ferramentas
  const getTileFromEvent = (e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } | null => {
    if (!currentMap) return null;
    const canvas = overlayCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;

    const tx = Math.floor(mouseX / TILE_SIZE);
    const ty = Math.floor(mouseY / TILE_SIZE);

    if (tx < 0 || tx >= currentMap.width || ty < 0 || ty >= currentMap.height) return null;
    return { x: tx, y: ty };
  };

  // Pintar uma célula de terreno
  // --- portais e baús -------------------------------------------------------

  /**
   * Um clique no modo "Portais & Baús".
   *
   * A gramática é a mesma dos dois: clicar numa casa VAZIA põe a peça, clicar
   * numa casa que já tem a peça a tira. O portal só se distingue por precisar
   * de dois cliques — ele é um par de bocas, e uma boca sozinha não liga nada.
   */
  const handleLinkClick = (tile: { x: number; y: number }) => {
    if (!currentMap) return;

    if (linkTool === 'bau') {
      const chests = currentMap.chests ?? [];
      const found = chests.findIndex((c) => c.at.x === tile.x && c.at.y === tile.y);
      if (found >= 0) {
        setCurrentMap({ ...currentMap, chests: chests.filter((_, i) => i !== found) });
        setStatus(`Baú removido de (${tile.x}, ${tile.y}).`);
        return;
      }
      const prize = chestPrizeFrom(chestPrizeKey);
      if (!prize) {
        setStatus('Nenhum item no acervo para pôr dentro do baú.');
        return;
      }
      setCurrentMap({
        ...currentMap,
        chests: [
          ...chests,
          { at: { x: tile.x, y: tile.y }, ...prize, ...(chestLocked ? {} : { locked: false }) },
        ],
      });
      setStatus(
        `Baú com ${chestPrizeName(prize)} em (${tile.x}, ${tile.y})${
          chestLocked ? ' — trancado' : ' — destrancado'
        }.`,
      );
      return;
    }

    const links = currentMap.teleports ?? [];
    // Clicar numa boca existente apaga o PAR inteiro: meia ligação não é
    // ligação, e deixar a outra ponta órfã no mapa é um erro invisível.
    const hit = links.findIndex(
      (t) =>
        (t.a.x === tile.x && t.a.y === tile.y) || (t.b.x === tile.x && t.b.y === tile.y),
    );
    if (hit >= 0) {
      setCurrentMap({ ...currentMap, teleports: links.filter((_, i) => i !== hit) });
      setTeleportDraft(null);
      setStatus('Portal removido — as duas bocas.');
      return;
    }

    if (!teleportDraft) {
      setTeleportDraft(tile);
      setStatus(`Entrada em (${tile.x}, ${tile.y}). Clique na casa de saída.`);
      return;
    }
    if (teleportDraft.x === tile.x && teleportDraft.y === tile.y) {
      setTeleportDraft(null);
      setStatus('Portal cancelado: a entrada e a saída eram a mesma casa.');
      return;
    }

    setCurrentMap({
      ...currentMap,
      teleports: [...links, { a: { ...teleportDraft }, b: { x: tile.x, y: tile.y } }],
    });
    setStatus(
      `Portal ligado: (${teleportDraft.x}, ${teleportDraft.y}) ↔ (${tile.x}, ${tile.y}).`,
    );
    setTeleportDraft(null);
  };

  /** Leva a unidade arrastada para a casa sob o mouse. */
  const dragPlacementTo = (tile: { x: number; y: number }) => {
    if (!currentMap || !dragging || dragging.kind !== 'placement') return;
    const placements = [...(currentMap.placements ?? [])];
    const p = placements[dragging.index];
    if (!p) return;
    if (p.at.x === tile.x && p.at.y === tile.y) return;
    // Evita empilhar unidades na mesma coordenada durante o arrasto
    const collision = placements.findIndex(
      (other, idx) => idx !== dragging.index && other.at.x === tile.x && other.at.y === tile.y,
    );
    if (collision >= 0) return;
    placements[dragging.index] = { ...p, at: { x: tile.x, y: tile.y } };
    setCurrentMap({ ...currentMap, placements });
  };

  /** Leva a peça arrastada para a casa sob o mouse. */
  const dragLinkTo = (tile: { x: number; y: number }) => {
    if (!currentMap || !dragging) return;

    if (dragging.kind === 'bau') {
      const chests = [...(currentMap.chests ?? [])];
      const chest = chests[dragging.index];
      if (!chest) return;
      if (chest.at.x === tile.x && chest.at.y === tile.y) return;
      chests[dragging.index] = { ...chest, at: { x: tile.x, y: tile.y } };
      setCurrentMap({ ...currentMap, chests });
      return;
    }

    if (dragging.kind === 'teleporte') {
      const links = [...(currentMap.teleports ?? [])];
      const link = links[dragging.index];
      if (!link) return;
      const moved = dragging.side === 'a' ? { ...link, a: tile } : { ...link, b: tile };
      if (moved.a.x === moved.b.x && moved.a.y === moved.b.y) return;
      links[dragging.index] = moved;
      setCurrentMap({ ...currentMap, teleports: links });
    }
  };

  /** A peça que está sob esta casa, para o arrasto saber o que pegar. */
  const linkAt = (tile: { x: number; y: number }) => {
    const chests = currentMap?.chests ?? [];
    const chestIdx = chests.findIndex((c) => c.at.x === tile.x && c.at.y === tile.y);
    if (chestIdx >= 0) return { kind: 'bau' as const, index: chestIdx };

    const links = currentMap?.teleports ?? [];
    for (const [index, link] of links.entries()) {
      if (link.a.x === tile.x && link.a.y === tile.y) {
        return { kind: 'teleporte' as const, index, side: 'a' as const };
      }
      if (link.b.x === tile.x && link.b.y === tile.y) {
        return { kind: 'teleporte' as const, index, side: 'b' as const };
      }
    }
    return null;
  };

  const paintTileTerrain = (x: number, y: number, terrainName: string) => {
    if (!currentMap) return;
    const { width, height, terrain, terrainTypes } = currentMap;
    if (x < 0 || x >= width || y < 0 || y >= height) return;

    let code = terrainTypes.indexOf(terrainName);
    let newTypes = [...terrainTypes];
    if (code === -1) {
      newTypes.push(terrainName);
      code = newTypes.length - 1;
    }

    const index = y * width + x;
    if (terrain[index] === code && newTypes.length === terrainTypes.length) return;

    const nextTerrain = [...terrain];
    nextTerrain[index] = code;

    setCurrentMap({
      ...currentMap,
      terrainTypes: newTypes,
      terrain: nextTerrain,
    });
  };

  /**
   * Pinta arte do mapa numa camada, reencaixando as bordas dos autotiles.
   *
   * Pintar um autotile não é escrever um número: é escrever o TIPO e depois
   * recalcular a forma da casa pintada e das oito ao redor, porque cada uma
   * delas passa a ter um vizinho diferente. Sem esse segundo passo a grama sai
   * como um tabuleiro de quadrados isolados — que é exatamente a aparência de
   * quem pinta autotile sem saber que existe forma.
   */
  const paintTileArt = (cells: readonly { x: number; y: number }[], tileId: number) => {
    if (!currentMap || cells.length === 0) return;
    const { width, height } = currentMap;

    const layers = currentMap.layers.map((l) => [...l]);
    const layer = layers[activeLayer];
    if (!layer) return;

    const inside = (x: number, y: number): boolean => x >= 0 && x < width && y >= 0 && y < height;
    const at = (x: number, y: number): number => (inside(x, y) ? (layer[y * width + x] ?? 0) : -1);

    let changed = false;
    for (const { x, y } of cells) {
      if (!inside(x, y)) continue;
      const index = y * width + x;
      if (layer[index] !== tileId) changed = true;
      layer[index] = tileId;
    }
    if (!changed) return;

    // A forma se recalcula na área pintada mais uma casa de margem: quem estava
    // na borda ganhou vizinho e precisa fechar o encaixe.
    const dirty = new Set<number>();
    for (const { x, y } of cells) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (inside(x + dx, y + dy)) dirty.add((y + dy) * width + (x + dx));
        }
      }
    }

    for (const index of dirty) {
      const x = index % width;
      const y = Math.floor(index / width);
      const self = layer[index] ?? 0;
      if (!isAutotile(self)) continue;

      // Fora do mapa conta como "mesmo tipo": senão toda borda do mapa viraria
      // uma quina, e o terreno pareceria recortado nas laterais.
      const joins = (dx: number, dy: number): boolean => {
        const nx = x + dx;
        const ny = y + dy;
        if (!inside(nx, ny)) return true;
        return sameFamily(self, at(nx, ny));
      };

      const nb: Neighbors = {
        n: joins(0, -1), s: joins(0, 1), e: joins(1, 0), w: joins(-1, 0),
        nw: joins(-1, -1), ne: joins(1, -1), sw: joins(-1, 1), se: joins(1, 1),
      };
      layer[index] = shapedTileId(self, nb);
    }

    setCurrentMap({ ...currentMap, layers });
    setTileRevision((r) => r + 1);
  };

  /** Balde na camada de arte: troca a região contígua do mesmo tile. */
  const floodFillArt = (startX: number, startY: number, tileId: number) => {
    if (!currentMap) return;
    const { width, height } = currentMap;
    const layer = currentMap.layers[activeLayer];
    if (!layer) return;

    const origin = layer[startY * width + startX] ?? 0;
    // Comparar por família, e não por número: uma grama pintada tem 48 formas
    // diferentes, e todas são "a mesma grama" para quem olha.
    if (sameFamily(origin, tileId)) return;

    const cells: { x: number; y: number }[] = [];
    const seen = new Uint8Array(width * height);
    const queue: { x: number; y: number }[] = [{ x: startX, y: startY }];

    while (queue.length > 0) {
      const cell = queue.pop()!;
      const { x, y } = cell;
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      const index = y * width + x;
      if (seen[index]) continue;
      if (!sameFamily(layer[index] ?? 0, origin)) continue;

      seen[index] = 1;
      cells.push(cell);
      queue.push({ x: x + 1, y }, { x: x - 1, y }, { x, y: y + 1 }, { x, y: y - 1 });
    }

    paintTileArt(cells, tileId);
  };

  // Preenchimento por inundação (Bucket Flood Fill)
  const floodFillTerrain = (startX: number, startY: number, newTerrainName: string) => {
    if (!currentMap) return;
    const { width, height, terrain, terrainTypes } = currentMap;
    const startIndex = startY * width + startX;
    const targetCode = terrain[startIndex] ?? 0;

    let fillCode = terrainTypes.indexOf(newTerrainName);
    let newTypes = [...terrainTypes];
    if (fillCode === -1) {
      newTypes.push(newTerrainName);
      fillCode = newTypes.length - 1;
    }

    if (targetCode === fillCode) return;

    const nextTerrain = [...terrain];
    const visited = new Uint8Array(width * height);
    const queue: Array<[number, number]> = [[startX, startY]];
    visited[startIndex] = 1;

    while (queue.length > 0) {
      const [cx, cy] = queue.pop()!;
      const idx = cy * width + cx;
      nextTerrain[idx] = fillCode;

      const dirs: Array<readonly [number, number]> = [
        [cx + 1, cy] as const,
        [cx - 1, cy] as const,
        [cx, cy + 1] as const,
        [cx, cy - 1] as const,
      ];

      for (const dir of dirs) {
        const nx = dir[0];
        const ny = dir[1];
        if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
          const nIdx = ny * width + nx;
          if (!visited[nIdx] && nextTerrain[nIdx] === targetCode) {
            visited[nIdx] = 1;
            queue.push([nx, ny]);
          }
        }
      }
    }

    setCurrentMap({
      ...currentMap,
      terrainTypes: newTypes,
      terrain: nextTerrain,
    });
  };

  // Preenchimento de Retângulo
  const fillRectTerrain = (x1: number, y1: number, x2: number, y2: number, terrainName: string) => {
    if (!currentMap) return;
    const { width, height, terrain, terrainTypes } = currentMap;

    let code = terrainTypes.indexOf(terrainName);
    let newTypes = [...terrainTypes];
    if (code === -1) {
      newTypes.push(terrainName);
      code = newTypes.length - 1;
    }

    const minX = Math.max(0, Math.min(x1, x2));
    const maxX = Math.min(width - 1, Math.max(x1, x2));
    const minY = Math.max(0, Math.min(y1, y2));
    const maxY = Math.min(height - 1, Math.max(y1, y2));

    const nextTerrain = [...terrain];
    for (let cy = minY; cy <= maxY; cy++) {
      for (let cx = minX; cx <= maxX; cx++) {
        nextTerrain[cy * width + cx] = code;
      }
    }

    setCurrentMap({
      ...currentMap,
      terrainTypes: newTypes,
      terrain: nextTerrain,
    });
  };

  // Interação do Mouse no Canvas Overlay
  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const tile = getTileFromEvent(e);
    if (!tile || !currentMap) return;

    // Se estiver selecionando o alvo do objetivo
    if (targetSelectMode) {
      setCurrentMap({
        ...currentMap,
        objective: {
          ...currentMap.objective,
          kind: 'tomar_posicao',
          at: { x: tile.x, y: tile.y },
        },
      });
      setTargetSelectMode(false);
      setNotification({
        type: 'info',
        text: `Posição alvo definida em (${tile.x}, ${tile.y}).`,
      });
      return;
    }

    setIsMouseDown(true);

    if (editMode === 'tiles') {
      // Borracha limpa a casa; nas camadas de objeto isso é o vazio (0), e na
      // camada de base também — deixar buraco é uma escolha legítima de mapa.
      const paint = tool === 'eraser' ? 0 : activeTile;
      if (tool === 'pencil' || tool === 'eraser') {
        paintTileArt([tile], paint);
      } else if (tool === 'rect') {
        setRectStart(tile);
      } else if (tool === 'bucket') {
        floodFillArt(tile.x, tile.y, paint);
      }
    } else if (editMode === 'terrain') {
      const terrainToApply = tool === 'eraser' ? 'planicie' : activeTerrain;
      if (tool === 'pencil' || tool === 'eraser') {
        paintTileTerrain(tile.x, tile.y, terrainToApply);
      } else if (tool === 'rect') {
        setRectStart(tile);
      } else if (tool === 'bucket') {
        floodFillTerrain(tile.x, tile.y, terrainToApply);
      }
    } else if (editMode === 'ligacoes') {
      /*
       * Um clique numa peça existente começa um ARRASTO, e só vira "apagar"
       * se o mouse soltar sem sair da casa. É o que torna mover e remover dois
       * gestos distintos com o mesmo botão — e é o que se espera de um editor.
       */
      const piece = linkAt(tile);
      if (piece) {
        setDragging(piece);
        setDragFrom(tile);
      } else {
        const existingPlacement = currentMap.placements?.findIndex(
          (p) => p.at.x === tile.x && p.at.y === tile.y,
        );
        if (existingPlacement !== undefined && existingPlacement >= 0) {
          setSelectedPlacementIdx(existingPlacement);
          setDragging({ kind: 'placement', index: existingPlacement });
          setDragFrom(tile);
          return;
        }
        handleLinkClick(tile);
      }
    } else if (editMode === 'placements') {
      // Verifica se clicou em uma unidade existente para arrastar ou editar
      const existingIdx = currentMap.placements?.findIndex(
        (p) => p.at.x === tile.x && p.at.y === tile.y,
      );

      if (existingIdx !== undefined && existingIdx >= 0) {
        setSelectedPlacementIdx(existingIdx);
        setDragging({ kind: 'placement', index: existingIdx });
        setDragFrom(tile);
      } else {
        // Se clicou num baú ou portal mesmo no modo de tropas, permite arrastar também
        const piece = linkAt(tile);
        if (piece) {
          setDragging(piece);
          setDragFrom(tile);
          return;
        }
        let newUnit: MapPlacement;
        if (activePlacementPreset.startsWith('char:')) {
          const charId = Number(activePlacementPreset.replace('char:', ''));
          const char = availableCharacters.find((c) => c.id === charId);
          const foundClass =
            availableClasses.find((cls) => cls.id === char?.classId)?.name ??
            availableClasses[0]?.name ??
            'Espadachim';
          const foundWeapon = char?.equipment?.[0]
            ? availableWeapons.find((w) => w.id === char.equipment?.[0])?.name ??
              availableWeapons[0]?.name ??
              'Espada Curta'
            : availableWeapons[0]?.name ?? 'Espada Curta';
          newUnit = {
            name: char?.name ?? `Heroi ${(currentMap.placements?.length ?? 0) + 1}`,
            team: placementTeam,
            className: foundClass,
            weaponName: foundWeapon,
            level: char?.level ?? 1,
            at: { x: tile.x, y: tile.y },
            portrait: char?.portrait,
            sprite: char?.mapSprite,
            battler: char?.battler,
            // Um personagem no lado da CPU pode perfeitamente ser o chefe do
            // mapa: é assim que se monta um duelo de heróis, e era isso que
            // faltava.
            isCommander:
              placementTeam === 'inimigo' && !currentMap.placements?.some((p) => p.isCommander),
            ...(placementTeam === 'jogador' ? {} : { aiProfile: 'agressivo' }),
          };
        } else if (activePlacementPreset.startsWith('enemy:')) {
          const enemyId = Number(activePlacementPreset.replace('enemy:', ''));
          const enemy = availableEnemies.find((e) => e.id === enemyId);
          const defaultClass = availableClasses[0]?.name ?? 'Espadachim';
          const defaultWeapon = availableWeapons[0]?.name ?? 'Espada Curta';
          newUnit = {
            name: enemy?.name ?? `Inimigo ${(currentMap.placements?.length ?? 0) + 1}`,
            // O lado é o que estiver escolhido na barra, inclusive Jogador: um
            // monstro recrutado é uma unidade como outra qualquer.
            team: placementTeam,
            className: defaultClass,
            weaponName: defaultWeapon,
            level: 1,
            at: { x: tile.x, y: tile.y },
            battler: enemy?.battler,
            // A folha escolhida no editor de inimigos. Ausente, `buildSkirmish`
            // aplica o sprite padrão do lado — que é como todo monstro entrava
            // em campo antes de o campo existir.
            sprite: enemy?.mapSprite,
            isCommander:
              placementTeam === 'inimigo' && !currentMap.placements?.some((p) => p.isCommander),
            ...(placementTeam === 'jogador'
              ? {}
              : { aiProfile: enemy?.aiProfile ?? 'agressivo' }),
          };
        } else {
          // Cria nova unidade no tile
          const defaultClass = availableClasses[0]?.name ?? 'Espadachim';
          const defaultWeapon = availableWeapons[0]?.name ?? 'Espada de Ferro';
          const count = (currentMap.placements?.length ?? 0) + 1;
          newUnit = {
            name: `${placementTeam === 'jogador' ? 'Heroi' : 'Inimigo'} ${count}`,
            team: placementTeam,
            className: defaultClass,
            weaponName: defaultWeapon,
            level: 1,
            at: { x: tile.x, y: tile.y },
            isCommander:
              placementTeam === 'inimigo' &&
              !currentMap.placements?.some((p) => p.isCommander),
            // Aliado é unidade da CPU tanto quanto inimigo: sem perfil, ele
            // caía no agressivo por omissão lá no motor, sem ninguém escolher.
            ...(placementTeam === 'jogador' ? {} : { aiProfile: 'agressivo' }),
          };
        }

        const nextPlacements = [...(currentMap.placements ?? []), newUnit];
        setCurrentMap({
          ...currentMap,
          placements: nextPlacements,
        });
        setSelectedPlacementIdx(nextPlacements.length - 1);
      }
    }
  };

  const handleOverlayMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const tile = getTileFromEvent(e);
    setHoverTile(tile);

    if (isMouseDown && tile && editMode === 'terrain') {
      const terrainToApply = tool === 'eraser' ? 'planicie' : activeTerrain;
      if (tool === 'pencil' || tool === 'eraser') {
        paintTileTerrain(tile.x, tile.y, terrainToApply);
      }
    }

    if (isMouseDown && tile && editMode === 'tiles' && (tool === 'pencil' || tool === 'eraser')) {
      paintTileArt([tile], tool === 'eraser' ? 0 : activeTile);
    }

    // Arrastar move a peça de portal, de baú ou tropa, casa a casa.
    if (isMouseDown && tile && dragging) {
      if (dragging.kind === 'placement') {
        dragPlacementTo(tile);
      } else {
        dragLinkTo(tile);
      }
    }
  };

  const handleOverlayMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isMouseDown) return;
    setIsMouseDown(false);

    if (dragging) {
      const tile = getTileFromEvent(e) ?? hoverTile;
      const currentDrag = dragging;
      const origin = dragFrom;
      setDragging(null);
      setDragFrom(null);

      if (currentDrag.kind === 'placement') {
        return;
      }

      // Soltar sem ter saído da casa não era arrasto, era clique — e clique em
      // peça existente remove.
      const parado = tile && origin && tile.x === origin.x && tile.y === origin.y;
      if (parado && tile && editMode === 'ligacoes') {
        handleLinkClick(tile);
      }
      return;
    }

    if (editMode === 'terrain' && tool === 'rect' && rectStart) {
      const tile = getTileFromEvent(e) ?? hoverTile;
      if (tile) {
        const terrainToApply = activeTerrain;
        fillRectTerrain(rectStart.x, rectStart.y, tile.x, tile.y, terrainToApply);
      }
      setRectStart(null);
    }

    if (editMode === 'tiles' && tool === 'rect' && rectStart) {
      const tile = getTileFromEvent(e) ?? hoverTile;
      if (tile) {
        const cells: { x: number; y: number }[] = [];
        for (let y = Math.min(rectStart.y, tile.y); y <= Math.max(rectStart.y, tile.y); y++) {
          for (let x = Math.min(rectStart.x, tile.x); x <= Math.max(rectStart.x, tile.x); x++) {
            cells.push({ x, y });
          }
        }
        // Um retângulo é um traço só: pintar casa a casa recalcularia a forma
        // de cada uma contra um estado ainda pela metade.
        paintTileArt(cells, activeTile);
      }
      setRectStart(null);
    }
  };

  // 7. Ações de Salvar, Excluir e Jogar
  const handleSaveMap = async () => {
    if (!currentMap) return;
    if (!canPersist) {
      setNotification({ type: 'error', text: SAVE_BLOCKED_REASON(user !== null) });
      return;
    }
    setIsSaving(true);
    setNotification({ type: 'info', text: 'Salvando mapa no MariaDB…' });

    try {
      /*
       * A cópia de um mapa do acervo feita DENTRO de um capítulo leva o nome
       * dele.
       *
       * Salvar um mapa de arquivo cria uma cópia com id novo — e, com o nome
       * original, o autor terminava com dois "Oásis" na lista sem saber qual é
       * o do capítulo três. O corte em 64 é o da coluna no banco.
       */
      const toSave =
        chapterContext && !isEditorMapId(currentMap.id)
          ? {
              ...currentMap,
              name: `${currentMap.name} · Cap. ${chapterContext.chapterNumber ?? '?'}`.slice(0, 64),
            }
          : currentMap;

      const res = await saveMap(toSave);
      if (res.ok && res.map) {
        // O capítulo passa a apontar para a cópia. Sem isto ele continuaria
        // jogando o mapa original, sem nada do que acabou de ser editado.
        const moved = res.map.id !== currentMap.id;
        if (moved && chapterContext?.onMapChanged) {
          chapterContext.onMapChanged(res.map.id, res.map.name);
        }
        setNotification({
          type: 'success',
          text: moved && chapterContext
            ? `Mapa "${res.map.name}" (#${res.map.id}) salvo — o capítulo passa a jogar nele.`
            : `Mapa "${res.map.name}" (#${res.map.id}) salvo com sucesso!`,
        });
        // Atualiza a lista de mapas
        const refreshedList = await fetchMapList();
        setMapList(refreshedList);
        if (moved) {
          setSelectedMapId(res.map.id);
        }
      } else {
        setNotification({
          type: 'error',
          text: res.message ?? 'Falha ao salvar mapa.',
        });
      }
    } catch (e) {
      setNotification({
        type: 'error',
        text: `Erro ao salvar: ${e instanceof Error ? e.message : String(e)}`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Tira o mapa da lista — qualquer mapa, e não só os do editor.
   *
   * Excluir só valia na faixa do editor, e a lista de mapas só crescia: uma
   * importação de jogo despeja dezenas de mapas que o autor nunca vai usar e
   * não havia como tirá-los da frente. Agora vale para os três tipos, com
   * efeitos diferentes que o aviso explica antes: o mapa do editor some do
   * banco, e o de arquivo (acervo ou pacote) sai da lista mas continua no
   * disco — salvar de novo naquele id o traz de volta.
   */
  const handleDeleteMap = async () => {
    if (!currentMap?.id) {
      setNotification({ type: 'error', text: 'Salve o mapa antes de excluí-lo.' });
      return;
    }
    if (!canPersist) {
      setNotification({ type: 'error', text: SAVE_BLOCKED_REASON(user !== null) });
      return;
    }

    const fromEditor = isEditorMapId(currentMap.id);
    const warning = fromEditor
      ? `Excluir o mapa "${currentMap.name}" (#${currentMap.id})? Ele é do editor e será apagado do banco de dados para sempre.`
      : `Remover o mapa "${currentMap.name}" (#${currentMap.id}) do acervo? Ele sai da lista do editor, da escaramuça e do Modo História. O arquivo original continua no disco: salvar um mapa neste id o traz de volta.`;

    if (!confirm(warning)) {
      return;
    }

    try {
      const res = await deleteMap(currentMap.id);
      if (res.ok) {
        setNotification({
          type: 'success',
          text: res.hidden
            ? 'Mapa removido do acervo. Para trazê-lo de volta, abra-o por este id e salve.'
            : 'Mapa excluído com sucesso.',
        });
        const refreshed = await fetchMapList();
        setMapList(refreshed);
        // Para o primeiro mapa que sobrou, e não para um id fixo: com o Oásis
        // também excluível, voltar sempre para o 13 abriria um mapa que
        // acabou de sair da lista.
        setSelectedMapId(refreshed[0]?.id ?? 13);
      } else {
        setNotification({ type: 'error', text: res.message ?? 'Falha ao excluir.' });
      }
    } catch (e) {
      setNotification({ type: 'error', text: String(e) });
    }
  };

  /**
   * "Jogar este Mapa" nunca deveria depender de gravar.
   *
   * Quem tem permissão continua ganhando uma cópia persistida — é o caminho que
   * deixa o mapa na lista para todo mundo. Quem não tem joga o rascunho em
   * memória: a escaramuça é idêntica, só não sobrevive ao recarregar a página.
   * Antes, o convidado batia num alerta com o código cru `nao_autenticado` e
   * perdia o trabalho.
   */
  const handlePlayMap = async () => {
    if (!currentMap) return;
    if (hasCriticalErrors) {
      setNotification({
        type: 'error',
        text: 'Corrija as pendências táticas indicadas no painel antes de jogar.',
      });
      return;
    }

    if (!canPersist) {
      if (!onPlayDraft) return;
      setNotification({
        type: 'info',
        text: 'Testando o rascunho — este mapa não será gravado no servidor.',
      });
      onPlayDraft(toPlayableMap(currentMap));
      return;
    }

    // Mapa do acervo ou de jogo importado vira uma cópia customizada antes de
    // jogar, para que a edição não sobrescreva o original.
    let mapToPlayId = currentMap.id;
    if (!isEditorMapId(mapToPlayId)) {
      setIsSaving(true);
      setNotification({ type: 'info', text: 'Criando cópia jogável no MariaDB…' });
      const res = await saveMap({
        ...currentMap,
        id: undefined, // gera novo ID >= 1000
        name: currentMap.id ? `${currentMap.name} (Custom)` : currentMap.name,
      });
      setIsSaving(false);
      if (res.ok && res.map) {
        mapToPlayId = res.map.id;
        const refreshed = await fetchMapList();
        setMapList(refreshed);
        setSelectedMapId(res.map.id);
      } else {
        // Falhou a gravação, mas o mapa está pronto: joga o rascunho em vez de
        // devolver um erro e deixar o autor sem nada.
        setNotification({
          type: 'error',
          text: `${res.message ?? 'Falha ao gravar o mapa'} — jogando o rascunho sem salvar.`,
        });
        onPlayDraft?.(toPlayableMap(currentMap));
        return;
      }
    } else {
      await saveMap(currentMap);
    }

    if (onPlayMap && mapToPlayId) {
      onPlayMap(mapToPlayId);
    }
  };

  /**
   * Ambiente inicial de um mapa novo, pelo tileset escolhido.
   *
   * Um mapa recém-criado não tem nome que se leia nem terreno pintado; o
   * tileset é a única pista de onde ele se passa, e é melhor pista do que
   * nenhuma. O autor troca depois — mas não começa num calabouço com trilha de
   * campina.
   */
  const ambienceFor = (tilesetId: number): Pick<CustomMapData, 'bgm' | 'battleback' | 'battleback2'> => {
    switch (tilesetId) {
      case 1:
        return { bgm: 'Field1', battleback: 'Grassland', battleback2: 'Grassland' };
      case 3:
        return { bgm: 'Town1', battleback: 'Wood1', battleback2: 'Room1' };
      case 4:
        return { bgm: 'Dungeon1', battleback: 'Cobblestones4', battleback2: 'Stone3' };
      case 5:
      case 6:
        return { bgm: 'Scene2', battleback: 'Cyberspace', battleback2: 'DarkSpace' };
      default:
        return { bgm: 'Field2', battleback: 'Grassland', battleback2: 'Grassland' };
    }
  };

  // 8. Criação de Novo Mapa
  const handleCreateNewMap = () => {
    const size = newMapWidth * newMapHeight;
    const blankMap: CustomMapData = {
      name: newMapName,
      width: newMapWidth,
      height: newMapHeight,
      tilesetId: newMapTilesetId,
      layers: [
        new Array<number>(size).fill(3200), // Piso / grama padrão
        new Array<number>(size).fill(0),
        new Array<number>(size).fill(0),
        new Array<number>(size).fill(0),
      ],
      shadows: new Array<number>(size).fill(0),
      terrain: new Array<number>(size).fill(0),
      terrainTypes: Object.keys(TACTICAL_TERRAINS),
      placements: [],
      objective: { kind: 'derrotar_comandante' },
      ...ambienceFor(newMapTilesetId),
    };
    // As duas forças saem do mesmo semeador que anota os mapas do acervo, e com
    // os nomes que o acervo realmente tem.
    const seeded: CustomMapData = { ...blankMap, placements: seedPlacements(blankMap, defaultRoster) };

    setCurrentMap(seeded);
    setModalNewOpen(false);
    setSelectedMapId(0);
    setNotification({ type: 'success', text: `Novo mapa "${newMapName}" criado!` });
  };

  const selectedUnit =
    currentMap?.placements && selectedPlacementIdx !== null
      ? currentMap.placements[selectedPlacementIdx]
      : null;

  const currentHoverInfo = useMemo(() => {
    if (!currentMap || !hoverTile) return null;
    const { width, height, terrain, terrainTypes } = currentMap;
    if (hoverTile.x < 0 || hoverTile.x >= width || hoverTile.y < 0 || hoverTile.y >= height) return null;
    const tIdx = terrain[hoverTile.y * width + hoverTile.x] ?? 0;
    const tName = terrainTypes[tIdx] ?? 'planicie';
    const info: TerrainDefInfo = TACTICAL_TERRAINS[tName] ?? {
      name: tName,
      label: tName,
      color: 'transparent',
      icon: '🌱',
      cost: 1,
      avoid: 0,
      defense: 0,
      heal: 0,
    };
    return {
      tile: hoverTile,
      terrain: info,
    };
  }, [currentMap, hoverTile]);

  return (
    <div className="map-editor-page">
      {chapterContext && (
        <div className="chapter-context-banner">
          <div className="chapter-context-info">
            <span className="chapter-context-tag">Modo História</span>
            <strong className="chapter-context-title">
              Capítulo {chapterContext.chapterNumber ?? '?'}: {chapterContext.chapterTitle ?? 'Mapa do Capítulo'}
            </strong>
            {chapterContext.campaignName && (
              <span className="chapter-context-campaign">({chapterContext.campaignName})</span>
            )}
          </div>
          {chapterContext.onClose && (
            <button
              type="button"
              className="chapter-context-close-btn"
              onClick={chapterContext.onClose}
              title="Voltar ao Editor da História"
            >
              ← Voltar à História
            </button>
          )}
        </div>
      )}
      {/* BARRA SUPERIOR DO EDITOR */}
      <header className="editor-top-bar">
        <div className="editor-brand-zone">
          <span className="editor-brand-icon">🗺️</span>
          <div>
            <h2 className="editor-title">Editor de Mapas & Campanhas</h2>
            <span className="editor-subtitle">
              {currentMap ? `${currentMap.name} (${currentMap.width}×${currentMap.height})` : 'Carregando…'}
            </span>
          </div>
        </div>

        <div className="editor-map-selector-zone">
          <select
            className="editor-select"
            value={selectedMapId}
            onChange={(e) => setSelectedMapId(Number(e.target.value))}
          >
            <optgroup label="Mapas Customizados">
              {mapList
                .filter((m) => m.isCustom)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    ⭐ #{m.id} · {m.name} ({m.width}×{m.height})
                  </option>
                ))}
            </optgroup>
            <optgroup label="Mapas Nativos do Acervo">
              {mapList
                .filter((m) => !m.isCustom)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    #{m.id} · {m.name} ({m.width}×{m.height})
                  </option>
                ))}
            </optgroup>
          </select>

          <button
            type="button"
            className="editor-btn editor-btn-secondary"
            onClick={() => setModalNewOpen(true)}
            title="Criar novo mapa tático do zero"
          >
            ➕ Novo
          </button>
        </div>

        <div className="editor-actions-zone">
          {!canPersist && (
            <span className="editor-readonly-tag" title={SAVE_BLOCKED_REASON(user !== null)}>
              👁️ Rascunho local
            </span>
          )}

          <button
            type="button"
            className="editor-btn editor-btn-save"
            onClick={handleSaveMap}
            disabled={isSaving || !currentMap || !canPersist}
            title={canPersist ? 'Salvar alterações no MariaDB' : SAVE_BLOCKED_REASON(user !== null)}
          >
            💾 {isSaving ? 'Salvando…' : 'Salvar'}
          </button>

          {/*
            O botão de excluir vale para os três tipos de mapa.

            Antes ele ficava apagado fora da faixa do editor, e a lista de
            mapas só crescia — os 56 do acervo mais dezenas por jogo
            importado, sem como tirar da frente os que não se usa. O que muda
            entre os tipos é o EFEITO, não a permissão, e a dica diz qual é.
          */}
          {canPersist && (
            <button
              type="button"
              className="editor-btn editor-btn-danger"
              onClick={handleDeleteMap}
              disabled={!currentMap?.id}
              title={
                isEditorMapId(currentMap?.id)
                  ? 'Excluir este mapa do editor (apagado do banco)'
                  : 'Remover este mapa do acervo (sai da lista; salvar neste id o traz de volta)'
              }
            >
              🗑️ Excluir
            </button>
          )}

          <button
            type="button"
            className={`editor-btn editor-btn-play ${hasCriticalErrors ? 'disabled' : ''}`}
            onClick={handlePlayMap}
            title={
              hasCriticalErrors
                ? 'Corrija as pendências táticas para jogar'
                : canPersist
                ? 'Iniciar escaramuça imediatamente com este mapa'
                : 'Testar o rascunho numa escaramuça (sem gravar no servidor)'
            }
          >
            🎮 Jogar este Mapa
          </button>
        </div>
      </header>

      {/* BARRA DE FERRAMENTAS E MODO */}
      <div className="editor-toolbar">
        <div className="toolbar-modes">
          <button
            type="button"
            className={`mode-btn ${editMode === 'tiles' ? 'active' : ''}`}
            onClick={() => {
              setEditMode('tiles');
              setTargetSelectMode(false);
            }}
            title="Desenhar a arte do mapa com os tiles do acervo"
          >
            🎨 Arte do Mapa
          </button>
          <button
            type="button"
            className={`mode-btn ${editMode === 'terrain' ? 'active' : ''}`}
            onClick={() => {
              setEditMode('terrain');
              setTargetSelectMode(false);
            }}
          >
            🌲 Terreno Tático
          </button>
          <button
            type="button"
            className={`mode-btn ${editMode === 'placements' ? 'active' : ''}`}
            onClick={() => {
              setEditMode('placements');
              setTargetSelectMode(false);
            }}
          >
            👥 Tropas & Unidades ({currentMap?.placements?.length ?? 0})
          </button>
          <button
            type="button"
            className={`mode-btn ${editMode === 'ligacoes' ? 'active' : ''}`}
            onClick={() => {
              setEditMode('ligacoes');
              setTargetSelectMode(false);
              setTeleportDraft(null);
            }}
            title="Portais que ligam cômodos separados e baús com itens"
          >
            🌀 Portais &amp; Baús ({(currentMap?.teleports?.length ?? 0) +
              (currentMap?.chests?.length ?? 0)})
          </button>
          <button
            type="button"
            className={`mode-btn ${editMode === 'objective' ? 'active' : ''}`}
            onClick={() => {
              setEditMode('objective');
              setTargetSelectMode(false);
            }}
          >
            🎯 Objetivo de Missão
          </button>
        </div>

        {editMode === 'terrain' && (
          <div className="toolbar-tools">
            <button
              type="button"
              className={`tool-btn ${tool === 'pencil' ? 'active' : ''}`}
              onClick={() => setTool('pencil')}
              title="Lápis (1 casa)"
            >
              ✏️ Lápis
            </button>
            <button
              type="button"
              className={`tool-btn ${tool === 'rect' ? 'active' : ''}`}
              onClick={() => setTool('rect')}
              title="Retângulo (arrastar área)"
            >
              🔲 Retângulo
            </button>
            <button
              type="button"
              className={`tool-btn ${tool === 'bucket' ? 'active' : ''}`}
              onClick={() => setTool('bucket')}
              title="Balde (preenchimento contíguo)"
            >
              🪣 Preencher
            </button>
            <button
              type="button"
              className={`tool-btn ${tool === 'eraser' ? 'active' : ''}`}
              onClick={() => setTool('eraser')}
              title="Borracha (limpa para Planície)"
            >
              🧹 Borracha
            </button>
          </div>
        )}

        {editMode === 'placements' && (
          <div className="toolbar-tools">
            <span className="toolbar-label">Lado:</span>
            <button
              type="button"
              className={`team-toggle-btn player ${placementTeam === 'jogador' ? 'active' : ''}`}
              onClick={() => setPlacementTeam('jogador')}
            >
              🔵 Jogador
            </button>
            <button
              type="button"
              className={`team-toggle-btn enemy ${placementTeam === 'inimigo' ? 'active' : ''}`}
              onClick={() => setPlacementTeam('inimigo')}
            >
              🔴 Inimigo
            </button>
            <button
              type="button"
              className={`team-toggle-btn ally ${placementTeam === 'aliado' ? 'active' : ''}`}
              onClick={() => setPlacementTeam('aliado')}
            >
              🟢 Aliado
            </button>

            <label className="toolbar-label" style={{ marginLeft: '0.6rem' }}>
              Unidade ao Clicar:
              <select
                className="form-select-sm"
                style={{ marginLeft: '0.4rem', maxWidth: '170px' }}
                value={activePlacementPreset}
                /*
                 * Quem se coloca e de que lado são escolhas separadas.
                 *
                 * Antes escolher um personagem do acervo forçava o lado para
                 * Jogador e escolher um monstro forçava para Inimigo — e com
                 * isso um mapa de heróis contra heróis era impossível de
                 * montar: bastava apontar o adversário para ele mudar de time.
                 * O lado agora é só dos botões acima.
                 */
                onChange={(e) => setActivePlacementPreset(e.target.value)}
              >
                <option value="padrao">Padrão da Facção</option>
                <optgroup label="👥 Personagens Cadastrados">
                  {availableCharacters.map((c) => (
                    <option key={`tb-char-${c.id}`} value={`char:${c.id}`}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="👹 Monstros Cadastrados">
                  {availableEnemies.map((en) => (
                    <option key={`tb-enemy-${en.id}`} value={`enemy:${en.id}`}>
                      {en.name}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>
          </div>
        )}

        <div className="toolbar-view-controls">
          <label className="toggle-label" title="Exibir linhas da grade">
            <input
              type="checkbox"
              checked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
            />
            Grade
          </label>
          <label className="toggle-label" title="Exibir cores táticas dos terrenos">
            <input
              type="checkbox"
              checked={showTactical}
              onChange={(e) => setShowTactical(e.target.checked)}
            />
            Terrenos
          </label>
          {showTactical && (
            <label className="toggle-label opacity-label" title="Intensidade do véu tático sobre a arte">
              <input
                type="range"
                min={10}
                max={100}
                step={5}
                value={tacticalOpacity}
                onChange={(e) => setTacticalOpacity(Number(e.target.value))}
              />
              <span className="opacity-value">{tacticalOpacity}%</span>
            </label>
          )}
          <label className="toggle-label" title="Exibir marcadores das tropas">
            <input
              type="checkbox"
              checked={showUnits}
              onChange={(e) => setShowUnits(e.target.checked)}
            />
            Tropas
          </label>

          <div className="zoom-controls">
            {/* Os afastamentos da partida, para conferir o traçado com o
                mesmo enquadramento que o jogador terá. O ajuste fino de -/+
                continua ao lado: quem desenha precisa de meio-termo. */}
            <div className="zoom-presets" role="group" aria-label="Afastamento padrão da câmera">
              {ZOOM_LEVELS.map((level) => (
                <button
                  key={level.id}
                  type="button"
                  className={`zoom-preset-btn ${Math.abs(zoom - level.scale) < 0.001 ? 'active' : ''}`}
                  onClick={() => setZoom(level.scale)}
                  aria-pressed={Math.abs(zoom - level.scale) < 0.001}
                  title={level.hint}
                >
                  {level.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="zoom-btn"
              onClick={() => setZoom((z) => Math.max(0.5, Number((z - 0.25).toFixed(2))))}
            >
              🔍 -
            </button>
            <span className="zoom-display">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              className="zoom-btn"
              onClick={() => setZoom((z) => Math.min(2, Number((z + 0.25).toFixed(2))))}
            >
              🔍 +
            </button>
          </div>
        </div>
      </div>

      {/* NOTIFICAÇÃO TOAST */}
      {notification && (
        <div className={`editor-notification ${notification.type}`}>
          <span>{notification.text}</span>
          <button type="button" onClick={() => setNotification(null)}>
            ✕
          </button>
        </div>
      )}

      {/* ÁREA DE TRABALHO: VIEWPORT DO MAPA + PAINEL LATERAL DINÂMICO */}
      <div className="editor-workspace">
        {/* VIEWPORT COM SCROLL E CANVAS DUPLO */}
        <div className="editor-viewport-container">
          <div
            className="editor-viewport"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              width: currentMap ? `${currentMap.width * TILE_SIZE}px` : 'auto',
              height: currentMap ? `${currentMap.height * TILE_SIZE}px` : 'auto',
            }}
          >
            {/* Camada Base PixiJS (Renderiza os autotiles originais) */}
            <div ref={hostRef} className="editor-pixi-host" />

            {/* Camada Interativa HTML5 Canvas (Grade, Overlays, Tropas, Seleção, Drag'n'Drop) */}
            <canvas
              ref={overlayCanvasRef}
              className="editor-overlay-canvas"
              style={{
                cursor: dragging
                  ? 'grabbing'
                  : hoverTile && (
                      (editMode === 'placements' && currentMap?.placements?.some((p) => p.at.x === hoverTile.x && p.at.y === hoverTile.y)) ||
                      linkAt(hoverTile) !== null
                    )
                  ? 'grab'
                  : undefined,
              }}
              onMouseDown={handleOverlayMouseDown}
              onMouseMove={handleOverlayMouseMove}
              onMouseUp={handleOverlayMouseUp}
              onMouseLeave={() => {
                setHoverTile(null);
                if (dragging) {
                  setDragging(null);
                  setDragFrom(null);
                }
              }}
            />
          </div>
        </div>

        {/* PAINEL LATERAL DIREITO (CONFORME MODO SELECIONADO) */}
        <aside className="editor-sidebar">
          {/* MODO ARTE DO MAPA */}
          {editMode === 'tiles' && (
            <div className="sidebar-section">
              <h3 className="sidebar-title">Arte do Mapa</h3>
              <p className="sidebar-desc">
                Escolha a camada e o tile, e desenhe direto no mapa. Os autotiles
                (A1–A4) encaixam as bordas sozinhos conforme os vizinhos.
              </p>

              <div className="layer-picker">
                {([0, 1, 2, 3] as const).map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`layer-btn ${activeLayer === n ? 'active' : ''}`}
                    onClick={() => setActiveLayer(n)}
                    title={
                      n === 0
                        ? 'Camada base: o chão do mapa'
                        : `Camada ${n + 1}: objetos por cima`
                    }
                  >
                    {n === 0 ? 'Base' : `Cam. ${n + 1}`}
                  </button>
                ))}
              </div>

              {manifest && currentTileset ? (
                <TilePalette
                  manifest={manifest}
                  tileset={currentTileset}
                  selectedTile={activeTile}
                  onSelect={setActiveTile}
                />
              ) : (
                <p className="small">carregando acervo…</p>
              )}
            </div>
          )}

          {/* MODO TERRENO */}
          {editMode === 'terrain' && (
            <div className="sidebar-section">
              <h3 className="sidebar-title">Paleta de Terrenos Táticos</h3>
              <p className="sidebar-desc">
                Selecione o terreno e pinte no mapa. Cada terreno concede modificadores táticos em batalha:
              </p>
              <div className="terrain-palette-grid">
                {Object.values(TACTICAL_TERRAINS).map((t) => {
                  const isSelected = activeTerrain === t.name;
                  return (
                    <button
                      key={t.name}
                      type="button"
                      className={`terrain-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        setActiveTerrain(t.name);
                        if (tool === 'eraser') setTool('pencil');
                      }}
                      style={{ borderLeftColor: t.color }}
                    >
                      <div className="terrain-card-header">
                        <span className="terrain-icon">{t.icon}</span>
                        <strong className="terrain-name">{t.label}</strong>
                      </div>
                      <div className="terrain-stats">
                        <span title="Custo de Movimento">
                          🏃 Custo: <b>{t.impassable ? 'Imped.' : t.cost}</b>
                        </span>
                        <span title="Bônus de Evasão">
                          💨 Esq: <b>+{t.avoid}%</b>
                        </span>
                        <span title="Bônus de Defesa">
                          🛡️ Def: <b>+{t.defense}</b>
                        </span>
                        {t.heal > 0 && (
                          <span title="Regeneração por Turno">
                            💖 Cura: <b>+{t.heal}%</b>
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* MODO TROPAS (PLACEMENTS) */}
          {editMode === 'placements' && (
            <div className="sidebar-section">
              <h3 className="sidebar-title">Configuração de Tropas</h3>
              <p className="sidebar-desc">
                Clique para posicionar novas unidades, ou clique e arraste (drag'n'drop) para mover personagens, baús e portais.
              </p>

              {chapterContext?.recruits && chapterContext.recruits.length > 0 && (
                <div className="chapter-recruits-panel">
                  <h4 className="recruits-title">✨ Recrutáveis da História</h4>
                  <p className="recruits-desc">
                    Personagens que entram no esquadrão através de conversa ou eventos neste capítulo:
                  </p>
                  <div className="recruits-list">
                    {chapterContext.recruits.map((r) => {
                      const onMap = currentMap?.placements?.some(
                        (p) => p.name.toLowerCase() === r.name.toLowerCase(),
                      );
                      return (
                        <div key={r.characterId} className="chapter-recruit-item">
                          <div className="recruit-info">
                            <strong className="recruit-name">{r.name}</strong>
                            {r.recruitmentCondition && (
                              <span className="recruit-cond">{r.recruitmentCondition}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            className={`btn-recruit-place ${onMap ? 'placed' : ''}`}
                            onClick={() => {
                              setActivePlacementPreset(`char:${r.characterId}`);
                              setPlacementTeam('inimigo');
                            }}
                            title="Preparar para posicionar no mapa como oponente recrutável"
                          >
                            {onMap ? '✓ No Mapa' : '+ Posicionar'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedUnit && selectedPlacementIdx !== null ? (
                <div className="unit-editor-form">
                  <div className="form-header">
                    <h4>Editando Tropa #{selectedPlacementIdx + 1}</h4>
                    <button
                      type="button"
                      className="btn-delete-unit"
                      onClick={() => {
                        if (!currentMap || selectedPlacementIdx === null) return;
                        const next = currentMap.placements?.filter((_, i) => i !== selectedPlacementIdx) ?? [];
                        setCurrentMap({ ...currentMap, placements: next });
                        setSelectedPlacementIdx(null);
                      }}
                      title="Excluir esta unidade do mapa"
                    >
                      🗑️ Remover
                    </button>
                  </div>

                  {chapterContext?.recruits?.find(
                    (r) => r.name.toLowerCase() === selectedUnit.name.toLowerCase(),
                  ) && (
                    <div className="recruit-highlight-badge">
                      <span>✨ <strong>Unidade Recrutável da Campanha</strong></span>
                      <p>
                        {chapterContext.recruits.find(
                          (r) => r.name.toLowerCase() === selectedUnit.name.toLowerCase(),
                        )?.recruitmentCondition}
                      </p>
                    </div>
                  )}

                  <div className="db-preset-panel">
                    <label className="form-field">
                      <span>🎭 Importar do Banco de Dados:</span>
                      <select
                        className="form-select db-select"
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val || !currentMap || selectedPlacementIdx === null) return;
                          const [kind, idStr] = val.split(':');
                          const id = Number(idStr);
                          if (kind === 'char') {
                            const char = availableCharacters.find((c) => c.id === id);
                            if (char) {
                              const foundClass =
                                availableClasses.find((cls) => cls.id === char.classId)?.name ??
                                selectedUnit.className;
                              const foundWeapon = char.equipment?.[0]
                                ? availableWeapons.find((w) => w.id === char.equipment?.[0])?.name ??
                                  selectedUnit.weaponName
                                : selectedUnit.weaponName;
                              const next = [...(currentMap.placements ?? [])];
                              /*
                               * O lado da unidade não muda ao trocar quem ela é.
                               *
                               * Esta linha dizia `team: selectedUnit.team ===
                               * 'inimigo' ? 'jogador' : ...` — dar a um inimigo
                               * a cara de um personagem do acervo o passava
                               * para o seu time na mesma hora. Era por isso que
                               * só monstro conseguia ser inimigo.
                               */
                              next[selectedPlacementIdx] = {
                                ...selectedUnit,
                                name: char.name,
                                className: foundClass,
                                weaponName: foundWeapon,
                                level: char.level ?? 1,
                                portrait: char.portrait,
                                sprite: char.mapSprite,
                                battler: char.battler,
                              };
                              setCurrentMap({ ...currentMap, placements: next });
                            }
                          } else if (kind === 'enemy') {
                            const enemy = availableEnemies.find((en) => en.id === id);
                            if (enemy) {
                              const next = [...(currentMap.placements ?? [])];
                              // Idem para o monstro: ele assume a aparência e
                              // a ficha, e continua no lado onde já estava.
                              next[selectedPlacementIdx] = {
                                ...selectedUnit,
                                name: enemy.name,
                                battler: enemy.battler,
                                sprite: enemy.mapSprite,
                                ...(selectedUnit.team === 'jogador'
                                  ? {}
                                  : { aiProfile: selectedUnit.aiProfile ?? enemy.aiProfile ?? 'agressivo' }),
                              };
                              setCurrentMap({ ...currentMap, placements: next });
                            }
                          }
                        }}
                      >
                        <option value="">-- Escolher Personagem ou Monstro --</option>
                        {/* Qualquer um dos dois serve para qualquer facção: o
                            lado é o campo logo abaixo, e não a lista. */}
                        <optgroup label="👥 Personagens Cadastrados">
                          {availableCharacters.map((c) => (
                            <option key={`char-opt-${c.id}`} value={`char:${c.id}`}>
                              {c.name} (Nv {c.level ?? 1})
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="👹 Monstros Cadastrados">
                          {availableEnemies.map((e) => (
                            <option key={`enemy-opt-${e.id}`} value={`enemy:${e.id}`}>
                              {e.name}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </label>
                  </div>

                  <label className="form-field">
                    <span>Nome da Unidade:</span>
                    <input
                      type="text"
                      className="form-input"
                      value={selectedUnit.name}
                      onChange={(e) => {
                        if (!currentMap || selectedPlacementIdx === null) return;
                        const next = [...(currentMap.placements ?? [])];
                        next[selectedPlacementIdx] = { ...selectedUnit, name: e.target.value };
                        setCurrentMap({ ...currentMap, placements: next });
                      }}
                    />
                  </label>

                  <label className="form-field">
                    <span>Equipe / Facção:</span>
                    <select
                      className="form-select"
                      value={selectedUnit.team}
                      onChange={(e) => {
                        if (!currentMap || selectedPlacementIdx === null) return;
                        const team = e.target.value as 'jogador' | 'inimigo' | 'aliado';
                        const next = [...(currentMap.placements ?? [])];
                        next[selectedPlacementIdx] = {
                          ...selectedUnit,
                          team,
                          // Aliado é da CPU tanto quanto inimigo, e um perfil
                          // já escolhido não se perde ao trocar de lado.
                          ...(team === 'jogador'
                            ? { aiProfile: undefined, isCommander: selectedUnit.isCommander }
                            : { aiProfile: selectedUnit.aiProfile ?? 'agressivo' }),
                        };
                        setCurrentMap({ ...currentMap, placements: next });
                      }}
                    >
                      <option value="jogador">🔵 Jogador (Controlável)</option>
                      <option value="inimigo">🔴 Inimigo (IA)</option>
                      <option value="aliado">🟢 Aliado (Verde / Suporte)</option>
                    </select>
                  </label>

                  {/* O comportamento da CPU vale para inimigo e para aliado —
                      os dois são jogados pela máquina. */}
                  {selectedUnit.team !== 'jogador' && (
                    <label className="form-field">
                      <span>🧠 Comportamento da CPU:</span>
                      <select
                        className="form-select"
                        value={selectedUnit.aiProfile ?? 'agressivo'}
                        onChange={(e) => {
                          if (!currentMap || selectedPlacementIdx === null) return;
                          const next = [...(currentMap.placements ?? [])];
                          next[selectedPlacementIdx] = {
                            ...selectedUnit,
                            aiProfile: e.target.value,
                          };
                          setCurrentMap({ ...currentMap, placements: next });
                        }}
                      >
                        {AI_PROFILES.map((profile) => (
                          <option key={profile.id} value={profile.id}>
                            {profile.label}
                          </option>
                        ))}
                      </select>
                      <small className="form-hint">
                        {AI_PROFILES.find((p) => p.id === (selectedUnit.aiProfile ?? 'agressivo'))?.hint}
                      </small>
                    </label>
                  )}

                  <div className="form-row-2">
                    <label className="form-field">
                      <span>Classe:</span>
                      <select
                        className="form-select"
                        value={selectedUnit.className}
                        onChange={(e) => {
                          if (!currentMap || selectedPlacementIdx === null) return;
                          const next = [...(currentMap.placements ?? [])];
                          next[selectedPlacementIdx] = { ...selectedUnit, className: e.target.value };
                          setCurrentMap({ ...currentMap, placements: next });
                        }}
                      >
                        {availableClasses.map((c) => (
                          <option key={c.id} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="form-field">
                      <span>Arma:</span>
                      <select
                        className="form-select"
                        value={selectedUnit.weaponName}
                        onChange={(e) => {
                          if (!currentMap || selectedPlacementIdx === null) return;
                          const next = [...(currentMap.placements ?? [])];
                          next[selectedPlacementIdx] = { ...selectedUnit, weaponName: e.target.value };
                          setCurrentMap({ ...currentMap, placements: next });
                        }}
                      >
                        {availableWeapons.map((w) => (
                          <option key={w.id} value={w.name}>
                            {w.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="form-row-2">
                    <label className="form-field">
                      <span>Nível (1-20):</span>
                      <input
                        type="number"
                        className="form-input"
                        min={1}
                        max={20}
                        value={selectedUnit.level ?? 1}
                        onChange={(e) => {
                          if (!currentMap || selectedPlacementIdx === null) return;
                          const next = [...(currentMap.placements ?? [])];
                          next[selectedPlacementIdx] = {
                            ...selectedUnit,
                            level: Number(e.target.value) || 1,
                          };
                          setCurrentMap({ ...currentMap, placements: next });
                        }}
                      />
                    </label>

                    <label className="form-field">
                      <span>Posição (X, Y):</span>
                      <div className="pos-inputs">
                        <input
                          type="number"
                          className="form-input-sm"
                          value={selectedUnit.at.x}
                          min={0}
                          max={(currentMap?.width ?? 1) - 1}
                          onChange={(e) => {
                            if (!currentMap || selectedPlacementIdx === null) return;
                            const next = [...(currentMap.placements ?? [])];
                            next[selectedPlacementIdx] = {
                              ...selectedUnit,
                              at: { ...selectedUnit.at, x: Number(e.target.value) || 0 },
                            };
                            setCurrentMap({ ...currentMap, placements: next });
                          }}
                        />
                        <span>×</span>
                        <input
                          type="number"
                          className="form-input-sm"
                          value={selectedUnit.at.y}
                          min={0}
                          max={(currentMap?.height ?? 1) - 1}
                          onChange={(e) => {
                            if (!currentMap || selectedPlacementIdx === null) return;
                            const next = [...(currentMap.placements ?? [])];
                            next[selectedPlacementIdx] = {
                              ...selectedUnit,
                              at: { ...selectedUnit.at, y: Number(e.target.value) || 0 },
                            };
                            setCurrentMap({ ...currentMap, placements: next });
                          }}
                        />
                      </div>
                    </label>
                  </div>

                  <label className="checkbox-field">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedUnit.isCommander)}
                      onChange={(e) => {
                        if (!currentMap || selectedPlacementIdx === null) return;
                        const next = [...(currentMap.placements ?? [])];
                        next[selectedPlacementIdx] = {
                          ...selectedUnit,
                          isCommander: e.target.checked,
                        };
                        setCurrentMap({ ...currentMap, placements: next });
                      }}
                    />
                    <strong>👑 É o Comandante / Chefe da Facção</strong>
                  </label>

                  {selectedUnit.team === 'inimigo' && (
                    <label className="form-field">
                      <span>Perfil de Inteligência Artificial:</span>
                      <select
                        className="form-select"
                        value={selectedUnit.aiProfile ?? 'agressivo'}
                        onChange={(e) => {
                          if (!currentMap || selectedPlacementIdx === null) return;
                          const next = [...(currentMap.placements ?? [])];
                          next[selectedPlacementIdx] = {
                            ...selectedUnit,
                            aiProfile: e.target.value,
                          };
                          setCurrentMap({ ...currentMap, placements: next });
                        }}
                      >
                        <option value="agressivo">⚔️ Agressivo (Avança em direção ao jogador)</option>
                        <option value="cauteloso">🛡️ Cauteloso (Só avança se tiver vantagem)</option>
                        <option value="guardiao">🏰 Guardião (Mantém a posição / defende o trono)</option>
                        <option value="emboscada">🌲 Emboscada (Ataca quando o jogador entrar no raio)</option>
                        <option value="passivo">🛑 Passivo (Não se move nem ataca)</option>
                      </select>
                    </label>
                  )}
                </div>
              ) : (
                <div className="no-selection-box">
                  <p className="muted">Nenhuma tropa selecionada.</p>
                  <p className="small">Clique em uma tropa no mapa ou em uma casa vazia para posicionar.</p>
                </div>
              )}

              {/* Lista compacta de tropas */}
              <div className="unit-list-box">
                <h4>Tropas em Campo ({currentMap?.placements?.length ?? 0}):</h4>
                <div className="unit-list-items">
                  {currentMap?.placements?.map((p, idx) => (
                    <div
                      key={idx}
                      className={`unit-list-item ${p.team} ${selectedPlacementIdx === idx ? 'active' : ''}`}
                      onClick={() => setSelectedPlacementIdx(idx)}
                    >
                      <span className="unit-team-dot" />
                      <span className="unit-info-txt">
                        <b>{p.name}</b> · {p.className} (Nv{p.level ?? 1}) · ({p.at.x},{p.at.y})
                      </span>
                      {p.isCommander && <span title="Comandante">👑</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* MODO PORTAIS & BAÚS */}
          {editMode === 'ligacoes' && (
            <div className="sidebar-section">
              <h3 className="sidebar-title">Portais &amp; Baús</h3>
              <p className="sidebar-desc">
                Clique numa casa vazia para pôr; arraste para mover; clique na peça para
                remover.
              </p>

              <div className="link-tool-row">
                <button
                  type="button"
                  className={`link-tool-btn ${linkTool === 'teleporte' ? 'active' : ''}`}
                  onClick={() => {
                    setLinkTool('teleporte');
                    setTeleportDraft(null);
                  }}
                >
                  🌀 Portal
                </button>
                <button
                  type="button"
                  className={`link-tool-btn ${linkTool === 'bau' ? 'active' : ''}`}
                  onClick={() => {
                    setLinkTool('bau');
                    setTeleportDraft(null);
                  }}
                >
                  🧰 Baú
                </button>
              </div>

              {linkTool === 'teleporte' ? (
                <>
                  <p className="sidebar-desc">
                    Um portal liga DUAS casas que a grade separa — é o que torna transitável
                    um mapa de RPG convertido em tabuleiro. Clique na entrada e depois na
                    saída.
                    {teleportDraft && (
                      <strong>
                        {' '}Entrada marcada em ({teleportDraft.x}, {teleportDraft.y}) — escolha a
                        saída.
                      </strong>
                    )}
                  </p>
                  <div className="link-list">
                    {(currentMap?.teleports ?? []).length === 0 && (
                      <p className="sidebar-desc">Nenhum portal neste mapa.</p>
                    )}
                    {(currentMap?.teleports ?? []).map((link, i) => (
                      <div key={`${link.a.x},${link.a.y}-${i}`} className="link-row">
                        <span className="link-row-id">#{i + 1}</span>
                        <span className="link-row-body">
                          ({link.a.x}, {link.a.y}) ↔ ({link.b.x}, {link.b.y})
                        </span>
                        <button
                          type="button"
                          className="link-row-del"
                          title="Remover este portal"
                          onClick={() => {
                            if (!currentMap) return;
                            setCurrentMap({
                              ...currentMap,
                              teleports: (currentMap.teleports ?? []).filter((_, j) => j !== i),
                            });
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="admin-form-row">
                    <label htmlFor="chest-item">Item dentro do próximo baú:</label>
                    <select
                      id="chest-item"
                      className="admin-select"
                      value={chestPrizeKey}
                      onChange={(e) => setChestPrizeKey(e.target.value)}
                    >
                      <option value="">(o primeiro do acervo)</option>
                      {/* Dois grupos porque são duas coisas diferentes: a arma
                          que se empunha e o item do RPG que se bebe. */}
                      <optgroup label="Itens (poção, antídoto, chave)">
                        {availableItems.map((i) => (
                          <option key={`i:${i.id}`} value={`i:${i.id}`}>
                            #{i.id}: {i.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Armas">
                        {availableWeapons.map((w) => (
                          <option key={`w:${w.id}`} value={`w:${w.id}`}>
                            #{w.id}: {w.name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  <label className="link-check">
                    <input
                      type="checkbox"
                      checked={chestLocked}
                      onChange={(e) => setChestLocked(e.target.checked)}
                    />
                    <span>
                      Trancado — só abre com <b>Chave de Baú</b> na bolsa ou com um{' '}
                      <b>ladrão/arruaceiro</b> no esquadrão.
                    </span>
                  </label>

                  <div className="link-list">
                    {(currentMap?.chests ?? []).length === 0 && (
                      <p className="sidebar-desc">Nenhum baú neste mapa.</p>
                    )}
                    {(currentMap?.chests ?? []).map((chest, i) => {
                      return (
                        <div key={`${chest.at.x},${chest.at.y}-${i}`} className="link-row">
                          <span className="link-row-id">
                            {chest.locked === false ? '📦' : '🧰'}
                          </span>
                          <span className="link-row-body">
                            ({chest.at.x}, {chest.at.y}) — {chestPrizeName(chest)}
                          </span>
                          <button
                            type="button"
                            className="link-row-del"
                            title="Remover este baú"
                            onClick={() => {
                              if (!currentMap) return;
                              setCurrentMap({
                                ...currentMap,
                                chests: (currentMap.chests ?? []).filter((_, j) => j !== i),
                              });
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* MODO OBJETIVO DE MISSÃO */}
          {editMode === 'objective' && (
            <div className="sidebar-section">
              <h3 className="sidebar-title">Objetivo da Vitória</h3>
              <p className="sidebar-desc">
                Defina como os jogadores vencem a escaramuça ou capítulo neste mapa:
              </p>

              <div className="objective-options">
                <label className="radio-card">
                  <input
                    type="radio"
                    name="obj_kind"
                    checked={currentMap?.objective?.kind === 'derrotar_todos'}
                    onChange={() => {
                      if (!currentMap) return;
                      setCurrentMap({
                        ...currentMap,
                        objective: { kind: 'derrotar_todos' },
                      });
                      setTargetSelectMode(false);
                    }}
                  />
                  <div className="radio-card-content">
                    <strong>⚔️ Derrotar Todos</strong>
                    <span>Elimine todas as unidades inimigas do campo de batalha.</span>
                  </div>
                </label>

                <label className="radio-card">
                  <input
                    type="radio"
                    name="obj_kind"
                    checked={currentMap?.objective?.kind === 'derrotar_comandante'}
                    onChange={() => {
                      if (!currentMap) return;
                      setCurrentMap({
                        ...currentMap,
                        objective: { kind: 'derrotar_comandante' },
                      });
                      setTargetSelectMode(false);
                    }}
                  />
                  <div className="radio-card-content">
                    <strong>👑 Derrotar o Comandante</strong>
                    <span>Derrote a unidade inimiga designada como Chefe / Comandante.</span>
                  </div>
                </label>

                <label className="radio-card">
                  <input
                    type="radio"
                    name="obj_kind"
                    checked={currentMap?.objective?.kind === 'tomar_posicao'}
                    onChange={() => {
                      if (!currentMap) return;
                      setCurrentMap({
                        ...currentMap,
                        objective: {
                          kind: 'tomar_posicao',
                          at: currentMap.objective?.at ?? {
                            x: Math.floor(currentMap.width / 2),
                            y: Math.floor(currentMap.height / 2),
                          },
                        },
                      });
                    }}
                  />
                  <div className="radio-card-content">
                    <strong>🎯 Tomar Posição / Trono</strong>
                    <span>Mova o comandante do jogador até a coordenada ou trono designado.</span>
                  </div>
                </label>

                {currentMap?.objective?.kind === 'tomar_posicao' && (
                  <div className="objective-subpanel">
                    <p className="small">
                      Coordenada do Trono/Alvo:{' '}
                      <b>
                        ({currentMap.objective.at?.x ?? 0}, {currentMap.objective.at?.y ?? 0})
                      </b>
                    </p>
                    <button
                      type="button"
                      className={`btn-select-target ${targetSelectMode ? 'active' : ''}`}
                      onClick={() => setTargetSelectMode((v) => !v)}
                    >
                      {targetSelectMode ? '📍 Clique na casa no mapa…' : '📍 Definir clicando no mapa'}
                    </button>
                  </div>
                )}

                <label className="radio-card">
                  <input
                    type="radio"
                    name="obj_kind"
                    checked={currentMap?.objective?.kind === 'sobreviver'}
                    onChange={() => {
                      if (!currentMap) return;
                      setCurrentMap({
                        ...currentMap,
                        objective: { kind: 'sobreviver', turns: 7 },
                      });
                      setTargetSelectMode(false);
                    }}
                  />
                  <div className="radio-card-content">
                    <strong>🛡️ Sobreviver</strong>
                    <span>Resista aos ataques inimigos por um número determinado de turnos.</span>
                  </div>
                </label>

                {currentMap?.objective?.kind === 'sobreviver' && (
                  <div className="objective-subpanel">
                    <label className="form-field">
                      <span>Quantidade de Turnos para Sobrevivência:</span>
                      <input
                        type="number"
                        className="form-input"
                        min={1}
                        max={30}
                        value={currentMap.objective.turns ?? 7}
                        onChange={(e) => {
                          if (!currentMap) return;
                          setCurrentMap({
                            ...currentMap,
                            objective: {
                              kind: 'sobreviver',
                              turns: Number(e.target.value) || 7,
                            },
                          });
                        }}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PAINEL DE VALIDAÇÃO TÁTICA EM TEMPO REAL */}
          <div className="sidebar-section validation-section">
            <h3 className="sidebar-title">
              Validador Tático {hasCriticalErrors ? '⚠️' : '✅'}
            </h3>
            {validationIssues.length === 0 ? (
              <div className="validation-success-box">
                <span>✅ Mapa taticamente equilibrado e pronto para combate!</span>
              </div>
            ) : (
              <ul className="validation-issues-list">
                {validationIssues.map((issue, idx) => (
                  <li key={idx} className={`issue-item ${issue.level}`}>
                    {issue.level === 'error' ? '❌' : '⚠️'} {issue.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>

      {/* BARRA INFERIOR DE STATUS E COORDENADAS */}
      <footer className="editor-status-bar">
        <div className="status-cell">
          <span>Status: <b>{status}</b></span>
        </div>
        <div className="status-cell">
          {currentHoverInfo ? (
            <span>
              📍 Casa <b>({currentHoverInfo.tile.x}, {currentHoverInfo.tile.y})</b> ·{' '}
              {currentHoverInfo.terrain.icon} <b>{currentHoverInfo.terrain.label}</b> (Custo:{' '}
              {currentHoverInfo.terrain.impassable ? 'Imped.' : currentHoverInfo.terrain.cost}, Esq:{' '}
              +{currentHoverInfo.terrain.avoid}%, Def: +{currentHoverInfo.terrain.defense})
            </span>
          ) : (
            <span className="muted">Posicione o cursor sobre o mapa</span>
          )}
        </div>
      </footer>

      {/* MODAL DE NOVO MAPA */}
      {modalNewOpen && (
        <div className="modal-overlay">
          <div className="modal-box new-map-modal">
            <div className="modal-header">
              <h3>Criar Novo Mapa Tático</h3>
              <button type="button" className="btn-close" onClick={() => setModalNewOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <label className="form-field">
                <span>Nome do Mapa:</span>
                <input
                  type="text"
                  className="form-input"
                  value={newMapName}
                  onChange={(e) => setNewMapName(e.target.value)}
                  placeholder="Ex: Planalto Sagrado"
                />
              </label>

              <div className="form-row-2">
                <label className="form-field">
                  <span>Largura (10 a 50 casas):</span>
                  <input
                    type="number"
                    className="form-input"
                    min={10}
                    max={50}
                    value={newMapWidth}
                    onChange={(e) => setNewMapWidth(Number(e.target.value) || 20)}
                  />
                </label>
                <label className="form-field">
                  <span>Altura (10 a 50 casas):</span>
                  <input
                    type="number"
                    className="form-input"
                    min={10}
                    max={50}
                    value={newMapHeight}
                    onChange={(e) => setNewMapHeight(Number(e.target.value) || 20)}
                  />
                </label>
              </div>

              <label className="form-field">
                <span>Tileset Gráfico:</span>
                <select
                  className="form-select"
                  value={newMapTilesetId}
                  onChange={(e) => setNewMapTilesetId(Number(e.target.value))}
                >
                  {tilesets.map((ts) => (
                    <option key={ts.id} value={ts.id}>
                      Tileset #{ts.id}: {ts.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="editor-btn editor-btn-secondary"
                onClick={() => setModalNewOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="editor-btn editor-btn-play"
                onClick={handleCreateNewMap}
              >
                Criar Mapa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

